import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import api, { API } from "../lib/api";
import MedicineRow from "../components/MedicineRow";
import LastVisitPanel from "../components/LastVisitPanel";
import LabTrendsChart from "../components/LabTrendsChart";
import { LabCatalogPicker, LabPackagePicker } from "../components/LabPickers";
import LiveCalcStrip from "../components/LiveCalcStrip";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { toast, Toaster } from "sonner";
import { detectPregnancy, pregnancyMedAlerts } from "../lib/pregnancySafety";
import GuidelineDoseStrip from "../components/GuidelineDoseStrip";
import { Save, Plus, Printer, MessageCircle, FileUp, AlertTriangle, Sparkles, ArrowLeft, Trash2, TrendingUp, Syringe, Send, Link2, Columns2, PanelRight, Maximize2, Copy, Pill, GitCompareArrows, FileCheck, Salad, Dumbbell, BookOpen, X, FlaskConical, Pencil, FileText, History, FileClock } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { CERT_LIST } from "../lib/certificateTypes";
import TemplatePicker from "../components/TemplatePicker";
import TrendCharts from "../components/TrendCharts";
import InsulinRow from "../components/InsulinRow";
import ReferralDialog from "../components/ReferralDialog";
import { useDraft, DraftBanner, saveDraftServer } from "../lib/draft";
import { rowQuality } from "../lib/doseCompat"; // MT-11B.7 / B.8
import MicButton from "../components/MicButton";
import AiExtractDialog from "../components/AiExtractDialog";
import TrendChip from "../components/TrendChip";
import CollapsibleSection from "../components/CollapsibleSection";
import VisitFlowRail from "../components/VisitFlowRail";
import ClinicalCalculators, { ckdEpi2021, loadPinnedCalcs } from "../components/ClinicalCalculators";
import GuidancePanel from "../components/GuidancePanel";
import VaccinesPlanner from "../components/VaccinesPlanner";
import { detectFDCs, applyFDC, applyAllFDCs, switchBrandTier, switchToMyChoice, restoreOriginal } from "../lib/brandTiers";
import { pickMyChoiceBrand } from "../lib/brandCompanies";
import { sameClassAlerts } from "../lib/drugClasses";
import { historyDdiAlerts } from "../lib/drugInteractions";
import { familyHistoryInsights, screeningStatus } from "../lib/familyHistory";
import { classifyAnaemia } from "../lib/anaemia";
import { PatientEducationHandouts } from "../components/PatientEducationHandouts";
import { PatientLanguagePreference } from "../components/PatientLanguagePreference";
import { PatientLanguageSelect } from "../components/PatientLanguageSelect";
import "./PatientDetailResponsive.css";
import InteractionCheckButton from "../components/InteractionCheckButton";
import UpcomingCalendar from "../components/UpcomingCalendar";
import SecondaryDiagnosisCombobox from "../components/SecondaryDiagnosisCombobox";
import ICD10Combobox from "../components/ICD10Combobox";
import AcuteSuggestionPanel from "../components/AcuteSuggestionPanel";
import { canonicalKey } from "../components/TrendChip";
import Vaccinations from "../components/Vaccinations";
import NextStepsPanel from "../components/NextStepsPanel";
import GLP1CompanionKitPanel from "../components/GLP1CompanionKitPanel";
import EduKitPanel from "../components/EduKitPanel";
import { EDU_KITS } from "../lib/eduKits";
import PostmenopausalBleedingBanner from "../components/PostmenopausalBleedingBanner";
import FamilyCard from "../components/FamilyCard";
import EditPatientDialog from "../components/EditPatientDialog";
import { checkBeforeAdd, monitoringPlanFor, auditRxTable, computeQuantity, titrationOptions, retitrateRow, applyDosePref, recordDosePrefs } from "../lib/prescribeSafety";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { CLINIC, doctorDisplay, resolveDoctor } from "../lib/clinic";
import { openWhatsApp } from "../lib/whatsapp";
import { LAB_CATALOG, LAB_LOOKUP, LAB_PACKAGES } from "../lib/labCatalog";
// PROV: Phase-1 observational provenance (fail-open — never alters clinical behaviour)
import { provLog, provSnapshot, flushProv } from "../lib/provenance";
import { useAuth } from "../lib/auth"; // PHASE-B1: prescriber binding
import { originOfRow, medEventFields, genId as provGenId } from "../lib/provenanceCore";

// Grouped so the picker can render section headers; the flat list drives the
// diagnosis-sync — every ticked chip becomes a bullet in Primary Diagnosis.
const KNOWN_DISEASE_GROUPS = [
  { label: "Endocrine / Metabolic", items: [
    "Type 2 DM", "Type 1 DM", "Prediabetes (IFG/IGT)", "Gestational DM",
    "Hypertension", "Dyslipidaemia", "Obesity", "Obesity (Post-Bariatric)", "Metabolic Syndrome",
    "Hypothyroidism", "Subclinical Hypothyroidism", "Hyperthyroidism", "Goitre / Thyroid Nodule",
    "PCOS", "Cushing's", "Addison's", "Hyperprolactinemia",
    "Vitamin D Deficiency", "Vitamin B12 Deficiency", "Hyperuricaemia",
    "Diabetic Neuropathy", "Diabetic Retinopathy", "Diabetic Nephropathy", "Hypoglycaemia (recurrent)",
  ]},
  { label: "Cardiovascular", items: [
    "CAD", "Post MI", "Post PCI / CABG", "Heart Failure (HFrEF)", "Heart Failure (HFpEF)",
    "Atrial Fibrillation", "Rheumatic Heart Disease", "Valvular Heart Disease", "Mechanical Valve Replacement",
    "Heart Block — 1st degree", "Heart Block — Mobitz I", "Heart Block — Mobitz II", "Complete Heart Block (3rd degree)",
    "Pacemaker in situ", "SVT / PSVT", "Long QT", "Sick Sinus / Bradycardia",
    "Cardiomyopathy", "Stroke / TIA", "Post CVA (Hemiparesis)", "Peripheral Arterial Disease", "DVT",
    "Pulmonary Embolism (past)", "Varicose Veins", "Pulmonary Hypertension",
  ]},
  { label: "Renal / Genitourinary", items: [
    "CKD Stage 1", "CKD Stage 2", "CKD Stage 3a", "CKD Stage 3b", "CKD Stage 4", "CKD Stage 5",
    "CKD on Dialysis", "Renal Transplant Recipient",
    "Proteinuria", "Nephrotic Syndrome", "Nephrolithiasis", "Recurrent UTI", "BPH",
    "Overactive Bladder / Incontinence", "Erectile Dysfunction",
  ]},
  { label: "Respiratory", items: [
    "Asthma", "COPD", "OSA", "Old Pulmonary TB", "Post-TB Sequelae (Fibrosis)",
    "Bronchiectasis", "ILD", "Allergic Bronchitis",
    "CO2 Retainer (Type 2 Resp Failure)", "AERD (Aspirin-sensitive Asthma)",
  ]},
  { label: "Hepatic / GI", items: [
    "Fatty Liver (MASLD)", "MASH", "Chronic Hepatitis B", "Chronic Hepatitis C", "Alcoholic Liver Disease",
    "Cirrhosis", "GERD", "Peptic Ulcer", "IBS", "Chronic Pancreatitis", "Gallstones", "IBD (UC/Crohn's)",
    "Constipation (Chronic)", "Haemorrhoids / Fissure", "Coeliac / Gluten Sensitivity", "H. pylori (treated)",
    "Portal HTN / Varices", "Hepatic Encephalopathy (past)", "GI Bleed (past)", "Oesophageal Stricture / Barrett's",
  ]},
  { label: "Musculoskeletal / Rheumatology", items: [
    "Osteoarthritis", "Rheumatoid Arthritis", "Ankylosing Spondylitis", "Gout",
    "SLE", "Psoriatic Arthritis", "Osteoporosis", "Fibromyalgia",
    "Cervical Spondylosis", "Lumbar Spondylosis / LBP", "Frozen Shoulder", "Plantar Fasciitis",
  ]},
  { label: "Neurology / Psych", items: [
    "Migraine", "Epilepsy", "Parkinson's", "Dementia", "Depression", "Anxiety", "Insomnia",
    "Vertigo (BPPV)", "Bipolar Disorder", "Schizophrenia (on Rx)", "Trigeminal Neuralgia",
  ]},
  { label: "Women's Health", items: [
    "Menopause / Perimenopausal", "Abnormal Uterine Bleeding", "Fibroid Uterus",
    "Infertility (under Rx)", "Recurrent Vaginal Candidiasis",
    "On OCP / HRT", "Pre-eclampsia (past)", "Endometriosis (on Rx)",
  ]},
  { label: "Haematology / Onc", items: [
    "Anaemia (IDA)", "Anaemia (Chronic Disease)", "Anaemia (Megaloblastic)",
    "Thalassaemia Trait", "Sickle Cell", "On Chemotherapy", "Breast Ca (Rx)", "Prostate Ca (Rx)",
  ]},
  { label: "Skin / Eye / Others", items: [
    "Psoriasis", "Eczema / Atopic Dermatitis", "Chronic Urticaria", "Fungal Infection (recurrent Tinea)",
    "Glaucoma", "Cataract (operated)", "Smoking / Tobacco Use", "Alcohol Use Disorder",
    "SJS/TEN (past drug reaction)", "Leprosy (on MDT)", "On Isotretinoin",
  ]},
  { label: "Infection / Other", items: [
    "HIV", "Old TB", "Chronic Sinusitis", "Allergic Rhinitis", "Post-Covid", "Chronic Fatigue Syndrome",
  ]},
];
const KNOWN_DISEASES = KNOWN_DISEASE_GROUPS.flatMap((g) => g.items);
const COMPLICATIONS = ["Diabetic Retinopathy", "Diabetic Nephropathy", "Diabetic Neuropathy", "Diabetic Foot", "PAD", "CAD", "CVD", "Heart Failure", "CKD", "Proteinuria", "Recurrent Hypoglycaemia"];
const ALLERGY_FLAGS = ["Drug Allergy", "Contrast Allergy", "Pregnancy", "Breastfeeding", "Renal Impairment", "Liver Impairment", "Heart Failure", "QT Prolongation", "Anticoagulant Use", "Elderly / Frail"];
const FOLLOWUP_PRESETS = ["3 days", "7 days", "15 days", "1 month", "3 months"];

// ============================================================================
// Investigation catalog — grouped by common Indian OPD packages. Each entry
// specifies the default unit and a typical reference range so a doctor can
// one-tap add an empty test row and have the staff fill only the result.
// ============================================================================

// (LAB_PACKAGES moved to /app/frontend/src/lib/labCatalog.js — shared with
// the reception's Manual Lab Entry so both roles pull the same profiles.)

// Ref-range parser for abnormal highlighting: "4-5.6", "< 14", "> 60".
const parseRefRange = (ref) => {
  if (!ref) return null;
  const s = String(ref).trim();
  let m = s.match(/^([\d.]+)\s*[-–]\s*([\d.]+)/);
  if (m) return { lo: parseFloat(m[1]), hi: parseFloat(m[2]) };
  m = s.match(/^<\s*=?\s*([\d.]+)/);
  if (m) return { hi: parseFloat(m[1]) };
  m = s.match(/^>\s*=?\s*([\d.]+)/);
  if (m) return { lo: parseFloat(m[1]) };
  return null;
};
const CATALOG_REF = (() => {
  const map = {};
  LAB_CATALOG.forEach((g) => g.tests.forEach((t) => { map[t.name.toLowerCase()] = t.ref; }));
  return map;
})();
const labAbnormal = (l) => {
  const r = parseRefRange(l.ref_range) || parseRefRange(CATALOG_REF[(l.test_name || "").toLowerCase()]);
  if (!r || l.value == null) return false;
  if (r.lo != null && l.value < r.lo) return true;
  if (r.hi != null && l.value > r.hi) return true;
  return false;
};

function bmiCategory(bmi) {
  if (!bmi) return "";
  if (bmi < 18.5) return "Underweight";
  if (bmi < 25) return "Normal";
  if (bmi < 30) return "Overweight";
  if (bmi < 35) return "Obesity Class I";
  if (bmi < 40) return "Obesity Class II";
  return "Obesity Class III";
}

function vitalsAlerts(v) {
  const a = [];
  const sbp = parseFloat(v.sbp), dbp = parseFloat(v.dbp);
  if (sbp >= 180 || dbp >= 120) a.push({ level: "critical", msg: "Severe hypertension" });
  else if (sbp >= 140 || dbp >= 90) a.push({ level: "warning", msg: "Elevated blood pressure" });
  if (sbp && sbp < 90) a.push({ level: "critical", msg: "Hypotension" });
  const pulse = parseFloat(v.pulse);
  if (pulse > 120) a.push({ level: "warning", msg: "Tachycardia" });
  if (pulse && pulse < 50) a.push({ level: "warning", msg: "Bradycardia" });
  const spo2 = parseFloat(v.spo2);
  if (spo2 && spo2 < 92) a.push({ level: "critical", msg: "Hypoxia (SpO₂ low)" });
  const temp = parseFloat(v.temperature);
  if (temp >= 39) a.push({ level: "warning", msg: "High fever" });
  const rbs = parseFloat(v.random_sugar);
  if (rbs >= 300) a.push({ level: "critical", msg: "Severe hyperglycaemia" });
  if (rbs && rbs < 70) a.push({ level: "critical", msg: "Hypoglycaemia" });
  return a;
}

function drugSafety(medicines, allergyFlags) {
  const alerts = [];
  const generics = new Set();
  medicines.forEach((m, i) => {
    if (!m.generic_name) return;
    const g = m.generic_name.toLowerCase();
    if (generics.has(g)) alerts.push({ level: "major", msg: `Duplicate generic: ${m.generic_name} (row ${i + 1})` });
    generics.add(g);
    // NSAID + anticoagulant
    if (allergyFlags.includes("Anticoagulant Use") && /nsaid|ibuprofen|diclofenac|naproxen|etoricoxib/i.test(m.generic_name + " " + m.composition)) {
      alerts.push({ level: "critical", msg: `NSAID with anticoagulant (${m.brand_name})` });
    }
    if (allergyFlags.includes("Renal Impairment") && /metformin/i.test(m.generic_name)) {
      alerts.push({ level: "caution", msg: `Metformin in renal impairment (${m.brand_name})` });
    }
    if (allergyFlags.includes("Pregnancy") && /statin|ramipril|losartan|telmisartan|warfarin/i.test(m.generic_name + " " + m.composition)) {
      alerts.push({ level: "critical", msg: `Contraindicated in pregnancy (${m.brand_name})` });
    }
  });
  return alerts;
}

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const emptyRow = () => ({ _uid: uid(), brand_name: "", generic_name: "", composition: "", strength: "", dose: "", route: "Oral", frequency: "", timing: "", duration: "", quantity: "", instructions: "" });
const emptyInv = () => ({ _uid: uid(), test_name: "", result: "", unit: "", reference_range: "", test_date: "" });

export default function PatientDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [patient, setPatient] = useState(null);
  const [prevVisits, setPrevVisits] = useState([]);
  const [reports, setReports] = useState([]);
  // Outbound WhatsApp log for this patient (manual wa.me shares included)
  const [waMessages, setWaMessages] = useState([]);
  // Screening Done tracker — per-rule date inputs before "Mark done"
  const [screeningDates, setScreeningDates] = useState({});
  // Paste-report panel (USG/CT/MRI/X-ray/PET/angio/bone-marrow text)
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteBusy, setPasteBusy] = useState(false);
  // Imaging key-finding chips the doctor dismissed this session (lowercased)
  const [removedFindings, setRemovedFindings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [latestLabs, setLatestLabs] = useState({});
  const [clonedFrom, setClonedFrom] = useState(null); // {visit_id, visit_date} banner
  const [continuationNote, setContinuationNote] = useState(null); // audit trail metadata

  // form state
  const [vitals, setVitals] = useState({});
  const [chief, setChief] = useState("");
  const [history, setHistory] = useState({ hpi: "", past_medical: "", drug_hx: "", family_hx: "", personal_hx: "", notes: "" });
  const [known, setKnown] = useState([]);
  const [syncKnownToDx, setSyncKnownToDx] = useState(true);
  const [complications, setComplications] = useState([]);
  const [allergyFlags, setAllergyFlags] = useState([]);
  // Extra patient-context guards used by drug-interaction & DDI checks —
  // alcoholMisuse, chronicSteroidUse, isImmunosuppressed, nitrateUse.
  // Kept as a single object for compact save/load.
  const [patientFlags, setPatientFlags] = useState({});
  const [allergyText, setAllergyText] = useState("");
  const [investigations, setInvestigations] = useState([emptyInv()]);
  const [diagnosis, setDiagnosis] = useState({ primary: "", secondary: "", icd10: "", status: "Under Evaluation", notes: "" });
  const [acuteCondition, setAcuteCondition] = useState(null);
  const [medicines, setMedicines] = useState([emptyRow()]);
  const [advice, setAdvice] = useState("");
  // Live estimated pharmacy bill (clinic price list) while building the Rx
  const [liveCost, setLiveCost] = useState(null);
  useEffect(() => {
    const real = medicines.filter((m) => m.brand_name || m.generic_name);
    if (!real.length) { setLiveCost(null); return; }
    const t = setTimeout(() => {
      api.post("/mydrugs/estimate-cost", { medicines: real })
        .then((r) => setLiveCost(r.data))
        .catch((e) => console.debug("[silent]", e));
    }, 900);
    return () => clearTimeout(t);
  }, [medicines]);
  // Compress & Cite — keeps a compressed, patient-facing summary alongside the
  // verbose detailed advice. `adviceView` decides which tab the doctor is on;
  // save-time picks the summary if the doctor left the Summary tab active.
  const [adviceSummary, setAdviceSummary] = useState("");
  const [adviceView, setAdviceView] = useState("detailed"); // 'detailed' | 'summary'
  const [adviceStyle, setAdviceStyle] = useState("prose");  // 'prose' | 'bullets'
  const [compressing, setCompressing] = useState(false);
  const [followUpDays, setFollowUpDays] = useState("15 days");
  // Selected doctor for THIS Rx — multi-doctor clinics only. Empty string
  // means "use the clinic default" (first doctor / legacy single-doctor).
  const [signedByDoctorId, setSignedByDoctorId] = useState("");
  const [nextInv, setNextInv] = useState("");
  const [nextSteps, setNextSteps] = useState(null); // structured AI Next-Steps accepted for print
  const [educationIds, setEducationIds] = useState([]); // patient education handouts ticked for print
  // Vaccines the doctor wants to add to this Rx with a specific appointment date.
  // Shape: [{ name: "Tdap booster", date: "2026-08-15" }]
  const [vaccinesPlanned, setVaccinesPlanned] = useState([]);
  // Vaccine catalog is loaded from the backend — age-aware (pediatric vs adult
  // schedule + travel/PEP extras). Cached in state to avoid a fetch per keystroke.
  const [vaccineCatalog, setVaccineCatalog] = useState([]);
  const [overrideAlerts, setOverrideAlerts] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  // Tap-time override audit trail — persisted on the prescription for medico-legal record
  const [safetyOverrides, setSafetyOverrides] = useState([]);
  // SJS/TEN culprit drug(s) — free text, arms an absolute re-exposure block
  const [sjsCulprit, setSjsCulprit] = useState("");
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastVisitId, setLastVisitId] = useState(null);
  // Edit-mode: when set, Save UPDATES this visit + prescription instead of
  // creating new ones (same-visit correction before handover).
  const [editTarget, setEditTarget] = useState(null); // {visit_id, prescription_id, visit_date}
  const { user: me } = useAuth(); // PHASE-B1: prescriber binding
  // PHASE-B1: default the prescriber to the logged-in doctor's LINKED profile —
  // never silently the clinic's first doctor. Edit mode keeps the saved value.
  // MT-11B.7: the linkage is server-resolved (tenant membership when MT is ON)
  // and re-applied whenever the auth context refreshes (fresh login, staff
  // link) — a stale/foreign selection is replaced, drafts never touch it.
  useEffect(() => {
    if (editTarget || !me?.doctor_id) return;
    const allowed = [me.doctor_id, ...(me.delegate_doctor_ids || [])];
    if (!signedByDoctorId || !allowed.includes(signedByDoctorId)) setSignedByDoctorId(me.doctor_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.doctor_id, (me?.delegate_doctor_ids || []).join("|")]);
  const [layout, setLayout] = useState(() => localStorage.getItem("pd-layout") || "wide"); // split | wide | full

  // ---- Draft autosave ----
  const draftState = { vitals, chief, history, known, complications, allergyFlags, patientFlags, allergyText, investigations, diagnosis, medicines, advice, adviceSummary, adviceView, adviceStyle, followUpDays, nextInv, safetyOverrides, sjsCulprit };
  const { clear: clearDraft } = useDraft(pid, draftState, (state, savedAt) => {
    if (!state) return;
    // Edit mode owns the form — never let a stale draft overwrite a loaded visit.
    if (new URLSearchParams(window.location.search).get("edit")) return;
    if (state.vitals) setVitals(state.vitals);
    if (typeof state.chief === "string") setChief(state.chief);
    if (state.history) setHistory(state.history);
    if (Array.isArray(state.known)) setKnown(state.known);
    if (Array.isArray(state.complications)) setComplications(state.complications);
    if (Array.isArray(state.allergyFlags)) setAllergyFlags(state.allergyFlags);
    if (state.patientFlags && typeof state.patientFlags === "object") setPatientFlags(state.patientFlags);
    if (Array.isArray(state.safetyOverrides)) setSafetyOverrides(state.safetyOverrides);
    if (typeof state.sjsCulprit === "string") setSjsCulprit(state.sjsCulprit);
    if (typeof state.allergyText === "string") setAllergyText(state.allergyText);
    if (Array.isArray(state.investigations) && state.investigations.length) setInvestigations(state.investigations);
    if (state.diagnosis) setDiagnosis(state.diagnosis);
    if (Array.isArray(state.medicines) && state.medicines.length) setMedicines(state.medicines);
    if (typeof state.advice === "string") setAdvice(state.advice);
    if (typeof state.adviceSummary === "string") setAdviceSummary(state.adviceSummary);
    if (state.adviceView === "detailed" || state.adviceView === "summary") setAdviceView(state.adviceView);
    if (state.adviceStyle === "prose" || state.adviceStyle === "bullets") setAdviceStyle(state.adviceStyle);
    if (typeof state.followUpDays === "string") setFollowUpDays(state.followUpDays);
    if (typeof state.nextInv === "string") setNextInv(state.nextInv);
    setDraftSavedAt(savedAt);
  });

  // Explicit cloud draft save — morning entry now, finish from any PC later.
  // Opens a tiny dialog for the optional "waiting for…" note first.
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [waitingFor, setWaitingFor] = useState("");
  const saveDraftNow = async () => {
    setSavingDraft(true);
    setDraftDialogOpen(false);
    try {
      await saveDraftServer(pid, draftState, waitingFor.trim());
      toast.success(waitingFor.trim()
        ? `Draft parked — waiting for: ${waitingFor.trim()}`
        : "Draft saved to cloud — resume from any PC via the Drafts page");
    } catch {
      toast.error("Could not save draft — check your connection");
    }
    setSavingDraft(false);
  };

  // Load a saved visit + its prescription back into the form for in-place editing.
  const loadVisitForEdit = (v, rx) => {
    setVitals(v.vitals || {});
    setChief(v.chief_complaints || "");
    if (v.clinical_history && typeof v.clinical_history === "object") setHistory({ hpi: "", past_medical: "", drug_hx: "", family_hx: "", personal_hx: "", notes: "", ...v.clinical_history });
    setKnown(Array.isArray(v.known_case_of) ? v.known_case_of : []);
    setComplications(Array.isArray(v.complications) ? v.complications : []);
    setAllergyFlags(Array.isArray(v.allergy_flags) ? v.allergy_flags : []);
    setPatientFlags(v.patient_flags && typeof v.patient_flags === "object" ? v.patient_flags : {});
    setAllergyText(v.allergy_text || "");
    setSjsCulprit(v.sjs_culprit || "");
    const invs = (v.investigations || []).map((x) => ({ ...emptyInv(), ...x, _uid: uid() }));
    setInvestigations(invs.length ? invs : [emptyInv()]);
    if (v.diagnosis) setDiagnosis({ primary: "", secondary: "", icd10: "", status: "Under Evaluation", notes: "", ...v.diagnosis });
    const meds = ((rx?.medicines || v.medicines) || []).map((m) => ({ ...emptyRow(), ...m, _uid: uid(), _prov: { origin: "UNKNOWN", detail: "carried_over" } })); // PROV: origin never inferred for pre-provenance rows (ruling D4)
    setMedicines(meds.length ? meds : [emptyRow()]);
    setAdvice(v.advice_detailed || rx?.advice_detailed || v.advice || rx?.advice || "");
    if (typeof v.follow_up === "string" && v.follow_up) setFollowUpDays(v.follow_up);
    setNextInv(v.next_investigations || "");
    setVaccinesPlanned(Array.isArray(v.vaccines_planned) ? v.vaccines_planned : []);
    if (Array.isArray(v.education_ids)) setEducationIds(v.education_ids);
    if (rx?.signed_by_doctor_id) setSignedByDoctorId(rx.signed_by_doctor_id);
    setContinuationNote(v.continuation || null);
  };

  useEffect(() => {
    const evid = searchParams.get("edit");
    if (!evid) return;
    (async () => {
      try {
        const r = await api.get(`/visits/${evid}`);
        loadVisitForEdit(r.data.visit, r.data.prescription);
        setEditTarget({
          visit_id: r.data.visit.visit_id,
          prescription_id: r.data.prescription?.prescription_id || null,
          visit_date: r.data.visit.visit_date,
        });
        toast.info("Editing saved visit — Save will update the same prescription");
      } catch {
        toast.error("Could not load the visit for editing");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clone the content of a prior visit into the working form.
  const cloneFromVisit = (v) => {
    if (!v) return;
    const meds = (v.medicines || []).map((m) => ({
      _uid: uid(),
      _prov: { origin: "UNKNOWN", detail: "carried_over" }, // PROV: origin never inferred for carried-over meds (ruling D4)
      brand_name: m.brand_name || "",
      generic_name: m.generic_name || "",
      composition: m.composition || "",
      strength: m.strength || "",
      dose: m.dose || "",
      route: m.route || "Oral",
      frequency: m.frequency || "",
      timing: m.timing || "",
      duration: m.duration || "",
      quantity: m.quantity || "",
      instructions: m.instructions || "",
    }));
    if (meds.length === 0) meds.push(emptyRow());
    setMedicines(meds);
    if (v.diagnosis) setDiagnosis((d) => ({
      ...d,
      primary: v.diagnosis.primary || d.primary,
      secondary: v.diagnosis.secondary || d.secondary,
      icd10: v.diagnosis.icd10 || d.icd10,
      status: v.diagnosis.status || d.status,
      notes: v.diagnosis.notes || d.notes,
    }));
    if (typeof v.advice === "string" && v.advice) setAdvice(v.advice);
    if (v.follow_up_after) setFollowUpDays(v.follow_up_after);
    if (typeof v.next_investigations === "string" && v.next_investigations) setNextInv(v.next_investigations);
    if (typeof v.chief_complaints === "string" && v.chief_complaints) setChief(v.chief_complaints);

    // Continuation note for the audit trail — prepended to the free-text
    // clinical notes field and shipped alongside the visit on save.
    const srcDate = v.visit_date ? new Date(v.visit_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "unknown date";
    const today = new Date().toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const drName = doctorDisplay(CLINIC.doctorName);
    const note = `[Continued treatment plan from ${srcDate} · cloned ${today} by ${drName}]`;
    setHistory((h) => ({ ...h, notes: h.notes ? `${note}\n${h.notes}` : note }));
    setContinuationNote({ source_visit_id: v.visit_id, source_visit_date: v.visit_date, cloned_at: new Date().toISOString(), doctor: drName, note });

    setClonedFrom({ visit_id: v.visit_id, visit_date: v.visit_date, medicine_count: meds.length, primary: v.diagnosis?.primary });

    // Fire-and-forget audit log so the clone event is captured even if the
    // doctor never saves the visit (e.g. drafted then abandoned).
    api.post("/audit/log", {
      action: "visit_cloned",
      patient_id: pid,
      details: { source_visit_id: v.visit_id, source_visit_date: v.visit_date, medicine_count: meds.length },
    }).catch(() => {});

    toast.success(`Cloned ${meds.length} medicine${meds.length !== 1 ? "s" : ""} from ${srcDate} — verify before saving`);
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/patients/${pid}`);
        setPatient(r.data.patient);
        // Load age-appropriate vaccine catalog for the Vaccines picker.
        try {
          const age = r.data.patient?.age;
          const q = age !== undefined && age !== null ? `?age=${age}` : "";
          const vc = await api.get(`/vaccines/catalog${q}`);
          setVaccineCatalog(vc.data.vaccines || []);
        } catch { /* non-fatal */ }
        setPrevVisits(r.data.visits || []);
        // Auto-carry Height forward — it doesn't change between visits.
        const lastWithVitals = (r.data.visits || []).find((v) => v.vitals && Object.values(v.vitals).some(Boolean));
        if (lastWithVitals?.vitals?.height && !searchParams.get("edit")) {
          setVitals((cur) => (cur.height ? cur : { ...cur, height: lastWithVitals.vitals.height }));
        }
        const rp = await api.get(`/reports/list/${pid}`);
        const reportsList = rp.data.reports || [];
        setReports(reportsList);
        api.get(`/wa/history/${pid}`).then((wm) => setWaMessages(wm.data.messages || [])).catch(() => {});
        // Auto-populate Investigations from the newest Lab Report(s) if the
        // current visit doesn't have any Investigation rows yet (typical
        // scenario: reception uploaded reports at check-in — the doctor
        // opens the patient and expects labs to be pre-filled).
        const hasRows = investigations.some((x) => x.test_name);
        if (!hasRows && !searchParams.get("edit")) {
          const labReports = reportsList
            .filter((r) => (r.extraction_status === "ok" || r.extraction_status === "manual") && (r.extracted_tests || []).length)
            // Only recent reports (last 30 days) to avoid pulling ancient data.
            .filter((r) => {
              try {
                const days = (Date.now() - new Date(r.uploaded_at).getTime()) / 86400000;
                return days <= 30;
              } catch { return true; }
            });
          if (labReports.length) {
            const merged = [];
            labReports.forEach((rep) => (rep.extracted_tests || []).forEach((t) => merged.push({
              ...t, report_date: rep.report_date || "", laboratory: rep.laboratory || "",
            })));
            if (merged.length) {
              addExtractedInvs(merged);
              const newest = labReports[0];
              toast.success(`Auto-loaded ${merged.length} lab values from ${newest.laboratory || "recent report"} (${newest.report_date || "date n/a"}) — verify before saving`);
            }
          }
        }
        try {
          const lr = await api.get(`/patients/${pid}/latest-investigations`);
          setLatestLabs(lr.data.latest || {});
        } catch (e) { console.debug("[silent]", e); }
        // Handle ?clone=<visit_id> — pre-fill from that visit (or "1" = last visit)
        const cloneParam = searchParams.get("clone");
        if (cloneParam) {
          const visits = r.data.visits || [];
          const source = cloneParam === "1" ? visits[0] : visits.find((x) => x.visit_id === cloneParam);
          if (source) cloneFromVisit(source);
          else toast.error("Could not find that previous visit to clone");
          // Remove the query param so a refresh doesn't re-clone
          setSearchParams({}, { replace: true });
        }
        // Auto-preload Known Case Of + Lab Package rules for a truly fresh visit.
        // Runs only when there's no draft in progress and no clone happening —
        // pulls the chronic-condition tags forward from the last visit and,
        // if any rule matches, drops the appropriate lab tests into the form
        // before the doctor touches the keyboard. Guarded by a ref so React
        // StrictMode's double-invocation of the effect doesn't fire the toast
        // (and dedupe pass) twice.
        if (!cloneParam && !autoPreloadRanRef.current) {
          autoPreloadRanRef.current = true;
          const lastKnown = ((r.data.visits || [])[0] || {}).known_case_of || [];
          const lastCulprit = ((r.data.visits || [])[0] || {}).sjs_culprit || "";
          if (lastCulprit) setSjsCulprit((cur) => cur || lastCulprit);
          // History carry-forward — PMH, drug & family history rarely change
          // between visits; silently pre-fill empty fields from the most
          // recent visit that recorded them. Never clobbers typed/draft text.
          if (!searchParams.get("edit")) {
            const lastHxVisit = (r.data.visits || []).find((v) => v.clinical_history && (v.clinical_history.past_medical || v.clinical_history.drug_hx || v.clinical_history.family_hx));
            const lastHx = lastHxVisit?.clinical_history;
            if (lastHx) {
              setHistory((h) => ({
                ...h,
                past_medical: h.past_medical || lastHx.past_medical || "",
                drug_hx: h.drug_hx || lastHx.drug_hx || "",
                family_hx: h.family_hx || lastHx.family_hx || "",
              }));
            }
          }
          if (lastKnown.length) {
            setKnown((cur) => (cur.length ? cur : lastKnown));
            try {
              const prefs = await api.get("/settings/lab-preferences");
              autoPreloadLabsFromPrefs(prefs.data.rules || [], lastKnown);
            } catch (e) { console.debug("[silent]", e); }
          }
        }
      } catch {
        toast.error("Failed to load patient");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  // Apply active lab-preference rules whose `match_any` overlaps with the
  // patient's known-case tags. Only fills empty investigation slots — never
  // clobbers rows the doctor already added. Called once at page load.
  const autoPreloadLabsFromPrefs = (rules, knownTags) => {
    if (!rules?.length || !knownTags?.length) return;
    const knownSet = new Set(knownTags.map((k) => (k || "").trim().toLowerCase()));
    const testsToAdd = new Map(); // preserve insertion order + dedupe
    const matchedRules = [];
    for (const rule of rules) {
      if (rule.active === false) continue;
      const matches = (rule.match_any || []).some((m) => knownSet.has((m || "").trim().toLowerCase()));
      if (!matches) continue;
      matchedRules.push(rule.trigger || "(unnamed)");
      // Expand packages → individual test names
      for (const pkgKey of rule.packages || []) {
        const pkg = LAB_PACKAGES.find((p) => p.key === pkgKey);
        if (!pkg) continue;
        for (const t of pkg.tests) testsToAdd.set(t, LAB_LOOKUP[t] || {});
      }
      for (const t of rule.tests || []) {
        if (!testsToAdd.has(t)) testsToAdd.set(t, LAB_LOOKUP[t] || {});
      }
    }
    if (!testsToAdd.size) return;
    setInvestigations((prev) => {
      const kept = (prev || []).filter((x) => x.test_name);
      const existingLower = new Set(kept.map((x) => x.test_name.toLowerCase()));
      const additions = [];
      for (const [name, meta] of testsToAdd) {
        if (existingLower.has(name.toLowerCase())) continue;
        additions.push({ _uid: uid(), test_name: name, result: "", unit: meta.unit || "", reference_range: meta.ref || "", test_date: new Date().toISOString().slice(0, 10) });
      }
      if (!additions.length) return prev;
      // Ask the user (non-blocking toast) — nothing is destructive here.
      toast.success(`Pre-loaded ${additions.length} tests from rule${matchedRules.length === 1 ? "" : "s"}: ${matchedRules.join(", ")}`);
      return [...kept, ...additions];
    });
  };

  // ------------------------------------------------------------------
  // Sync "Known Case Of" chips → Primary Diagnosis.
  // We remember what we previously auto-synced so we don't clobber the
  // doctor's manual edits: only overwrite the diagnosis primary when it
  // still matches the last auto-sync, or is empty. The decision is made
  // BEFORE `setDiagnosis` (using a live ref to diagnosis) so React 18
  // StrictMode's double-invocation of the state updater never runs mutation
  // logic twice — the fix for iteration_20 HIGH.
  // ------------------------------------------------------------------
  const lastSyncedDxRef = useRef("");
  const autoPreloadRanRef = useRef(false);
  const lastIntakeIdRef = useRef(null);

  // Live sync from Reception → doctor's PatientDetail. Every 6 seconds we
  // check for an unfinalised 'intake' visit and merge its vitals into the
  // doctor's form (only for keys the doctor hasn't already typed over).
  // A one-shot toast tells the doctor when new numbers arrived so nothing
  // ships silently in front of them.
  useEffect(() => {
    if (!pid) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/patients/${pid}/latest-intake`);
        if (cancelled) return;
        const intake = r.data?.intake;
        if (!intake) return;
        if (lastIntakeIdRef.current === intake.visit_id + "|" + intake.updated_at) return;
        lastIntakeIdRef.current = intake.visit_id + "|" + intake.updated_at;
        const iv = intake.vitals || {};
        setVitals((cur) => {
          const merged = { ...cur };
          let changed = 0;
          for (const k of ["weight", "height", "waist", "sbp", "dbp", "pulse", "spo2", "bmi", "temp"]) {
            if ((iv[k] != null && iv[k] !== "") && (!merged[k] || merged[k] === "")) {
              merged[k] = iv[k];
              changed++;
            }
          }
          if (changed) toast.info(`Reception updated ${changed} vital(s) live`);
          return merged;
        });
      } catch (e) { console.debug("[silent]", e); }
    };
    poll();
    const t = setInterval(poll, 6000);
    return () => { cancelled = true; clearInterval(t); };
  }, [pid]);
  const diagnosisRef = useRef(diagnosis);
  useEffect(() => { diagnosisRef.current = diagnosis; }, [diagnosis]);
  useEffect(() => {
    if (!syncKnownToDx) return;
    const next = known.join(", ");
    const cur = (diagnosisRef.current.primary || "").trim();
    if (cur === lastSyncedDxRef.current || cur === "") {
      setDiagnosis((prev) => ({ ...prev, primary: next }));
      lastSyncedDxRef.current = next;
    }
    // Manual-edit branch: intentionally DO NOT touch lastSyncedDxRef here.
    // Keeping the ref at the last auto-synced value guarantees that every
    // subsequent chip toggle (tick or untick) sees `cur !== ref` and
    // continues to preserve the doctor's manual text. Fix for iteration_21.
  }, [known, syncKnownToDx]);

  const bmi = useMemo(() => {
    const w = parseFloat(vitals.weight), h = parseFloat(vitals.height);
    if (!w || !h) return "";
    const m = h / 100;
    return (w / (m * m)).toFixed(2);
  }, [vitals.weight, vitals.height]);

  const vAlerts = useMemo(() => vitalsAlerts({ ...vitals, bmi }), [vitals, bmi]);
  const pregnancyMode = useMemo(
    () => detectPregnancy({ known, diagnosisPrimary: diagnosis.primary, diagnosisSecondary: diagnosis.secondary, allergyFlags }),
    [known, diagnosis.primary, diagnosis.secondary, allergyFlags]
  );
  // Shared context for the check-before-add guard + whole-table audit
  const bestEgfr = useMemo(() => {
    const fromLabs = parseFloat(latestLabs?.egfr?.value);
    if (fromLabs) return fromLabs;
    const scr = parseFloat(investigations.find((i) => /creat/i.test(i.test_name || ""))?.result);
    const a = parseFloat(patient?.age);
    return scr && a ? Math.round(ckdEpi2021(scr, a, patient?.sex)) : null;
  }, [latestLabs, investigations, patient]);
  // Imaging & procedure reports on file (USG/CT/MRI/echo/angio/bone marrow…)
  const imagingReports = useMemo(() => {
    const list = (reports || []).filter((r) => (r.report_kind === "imaging" || r.report_kind === "procedure") && r.imaging && (r.imaging.impression || (r.imaging.key_findings || []).length));
    // De-dupe identical studies (same study + date + impression) — e.g. the
    // same report pasted twice only lists once.
    const seen = new Set();
    return list.filter((r) => {
      const sig = `${(r.imaging.study || r.imaging.modality || "").toLowerCase()}|${r.report_date || ""}|${(r.imaging.impression || "").slice(0, 80).toLowerCase()}`;
      if (seen.has(sig)) return false;
      seen.add(sig);
      return true;
    });
  }, [reports]);
  // Deduped key findings from those reports — shown as removable chips and
  // fed into the deterministic safety engine (knownText) below. NEGATIVE
  // findings ("no pleural effusion", "unremarkable") are filtered out so they
  // can't false-trigger condition rules.
  const NEG_FINDING_RE = /^(no |nil |absent|unremarkable|normal\b|within normal)/i;
  const imagingFindings = useMemo(() => {
    const out = [];
    imagingReports.forEach((r) => (r.imaging.key_findings || []).forEach((f) => {
      const key = (f || "").trim();
      if (!key || NEG_FINDING_RE.test(key)) return;
      if (!out.some((x) => x.text.toLowerCase() === key.toLowerCase())) {
        out.push({ text: key, source: r.imaging.study || r.imaging.modality || "report", date: r.report_date || "" });
      }
    }));
    return out.filter((x) => !removedFindings.includes(x.text.toLowerCase()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagingReports, removedFindings]);
  // One-line medico-legal "Reports reviewed" summary saved on the visit and
  // printed on the Rx — study + date + headline finding per report.
  const imagingReviewed = useMemo(() => imagingReports.slice(0, 4).map((r) => ({
    study: r.imaging.study || r.imaging.modality || "Report",
    report_date: r.report_date || "",
    summary: (r.imaging.key_findings || [])[0] || (r.imaging.impression || "").slice(0, 80),
  })), [imagingReports]);
  // Anaemia auto-classifier — typed rows take priority, last labs fill gaps.
  const anaemiaCall = useMemo(() => {
    const get = (re, strict) => {
      const row = investigations.find((i) => i.test_name && re.test(i.test_name.trim()) && i.result !== "" && i.result != null);
      if (row) {
        const n = parseFloat(String(row.result).replace(/[^\d.]/g, ""));
        if (!isNaN(n)) return n;
      }
      for (const l of Object.values(latestLabs || {})) {
        if (l && l.test_name && re.test(String(l.test_name).trim()) && l.value != null) {
          const n = parseFloat(l.value);
          if (!isNaN(n)) return n;
        }
      }
      return null;
    };
    return classifyAnaemia({
      hb: get(/^ha?emoglobin$|^hb$/i),
      mcv: get(/^mcv$/i),
      rdw: get(/^rdw/i),
      retic: get(/reticulocyte/i),
      rbc: get(/^rbc count$/i),
      ferritin: get(/^ferritin$/i),
      b12: get(/b12/i),
      folate: get(/^folate$/i),
      female: String(patient?.sex || patient?.gender || "").toLowerCase().startsWith("f"),
    });
  }, [investigations, latestLabs, patient]);
  const guardCtx = useMemo(() => ({
    egfr: bestEgfr,
    age: patient?.age,
    weightKg: parseFloat(vitals.weight) || parseFloat(prevVisits.find((x) => parseFloat(x?.vitals?.weight))?.vitals?.weight) || null,
    allergyText,
    allergyFlags,
    medicines,
    // Patient's OWN free-text history feeds condition logic too (typed
    // "k/c/o type 2 diabetes" in PMH works like the chip). Family history is
    // deliberately excluded — a relative's disease must not alter dosing.
    knownText: `${known.join(" ")} ${diagnosis.primary} ${diagnosis.secondary} ${allergyFlags.join(" ")} ${chief || ""} ${history.hpi || ""} ${history.past_medical || ""} ${history.drug_hx || ""} ${imagingFindings.map((x) => x.text).join(" ")}${patientFlags.lactating ? " lactating" : ""}${patientFlags.g6pdDeficiency ? " g6pd deficiency" : ""}`.toLowerCase(),
    sjsCulprit,
    pregnancy: !!pregnancyMode,
  }), [bestEgfr, patient, vitals.weight, prevVisits, allergyText, allergyFlags, medicines, known, diagnosis.primary, diagnosis.secondary, chief, history, patientFlags, sjsCulprit, pregnancyMode, imagingFindings]);
  const dAlerts = useMemo(() => {
    const base = drugSafety(medicines, allergyFlags);
    // PROV: identical alert computation — sources gain an inert _cat tag used only for provenance logging
    return [...base.map((a) => ({ ...a, _cat: a._cat || null })), ...sameClassAlerts(medicines).map((a) => ({ ...a, _cat: "DUPLICATION" })), ...(pregnancyMode ? pregnancyMedAlerts(medicines).map((a) => ({ ...a, _cat: "PREGNANCY" })) : []), ...auditRxTable(medicines, guardCtx).map((a) => ({ ...a, _cat: a._cat || null })), ...historyDdiAlerts(medicines, history.drug_hx).map((a) => ({ ...a, _cat: "DDI" }))];
  }, [medicines, allergyFlags, pregnancyMode, guardCtx, history.drug_hx]);
  const criticalCount = [...vAlerts, ...dAlerts].filter((a) => a.level === "critical").length;

  // PROV: session id + one-time emit of newly-appearing table-level safety alerts (observational)
  const provSessionRef = useRef(provGenId());
  const provSeenAlertsRef = useRef(new Set());
  useEffect(() => {
    const fresh = dAlerts.filter((a) => {
      const k = `${a.level}|${a.rule_id || ""}|${a.msg || a.text || ""}`;
      if (!k.replace(/\|/g, "").trim() || provSeenAlertsRef.current.has(k)) return false;
      provSeenAlertsRef.current.add(k);
      return true;
    });
    if (fresh.length) provLog(fresh.map((a) => ({ event_type: "SAFETY_ALERT_FIRED", session_id: provSessionRef.current, patient_internal_id: pid, severity: a.level, warning_text: a.msg || a.text || "", safety_rule_id: a.rule_id || null, safety_category: a._cat || null })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dAlerts]);
  // PROV: safety events at the add-time guard (does not change guard behaviour)
  const provGuardIssues = (row, issues, detail) => {
    if (!issues?.length) return;
    provLog(issues.map((i) => ({ event_type: "SAFETY_ALERT_FIRED", session_id: provSessionRef.current, patient_internal_id: pid, medicine: medEventFields(row), severity: i.severity, warning_text: i.text, safety_category: null, origin_detail: detail })));
  };

  const toggle = (list, setList, item) => setList(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

  // ---- Screening Done tracker (family-history advice silencer) ----
  const todayStr = new Date().toISOString().slice(0, 10);
  const fmtScrDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "");
  const fmtScrMonth = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "");
  const saveScreenings = async (list) => {
    try {
      const r = await api.put(`/patients/${pid}/screenings`, { screenings_done: list });
      setPatient((p) => ({ ...p, screenings_done: r.data.screenings_done }));
      return true;
    } catch {
      toast.error("Could not save screening status");
      return false;
    }
  };
  const markScreeningDone = async (ruleId) => {
    const date = screeningDates[ruleId] || todayStr;
    const list = (patient?.screenings_done || []).filter((d) => d.rule_id !== ruleId).concat([{ rule_id: ruleId, done_date: date }]);
    if (await saveScreenings(list)) toast.success("Screening marked done — hidden from the Rx until due again");
  };
  const undoScreening = (ruleId) => saveScreenings((patient?.screenings_done || []).filter((d) => d.rule_id !== ruleId));

  // ---- Paste report (USG/CT/MRI/X-ray/PET/angio/bone marrow/lab text) ----
  const submitPaste = async () => {
    if (pasteText.trim().length < 30) { toast.error("Paste the full report text — too short to extract"); return; }
    setPasteBusy(true);
    try {
      const r = await api.post("/reports/paste", { patient_id: pid, text: pasteText });
      const rep = r.data.report;
      setReports((prev) => [rep, ...prev]);
      const im = rep.imaging || {};
      const labN = (rep.extracted_tests || []).length;
      toast.success(`Saved — ${im.study || im.modality || (rep.report_kind === "lab" ? "Lab report" : "Report")}${labN ? ` · ${labN} lab values extracted` : ""}${im.impression ? " · impression captured" : ""}`);
      setPasteText("");
      setPasteOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed — try again");
    }
    setPasteBusy(false);
  };

  const addRow = () => setMedicines([...medicines, { ...emptyRow(), _prov: { origin: "PHYSICIAN", detail: "free_text" } }]); // PROV: origin tag only
  const addInsulinRow = () => setMedicines([...medicines, { ...emptyRow(), type: "insulin", route: "SC", dosage_form: "Injection", _prov: { origin: "PHYSICIAN", detail: "free_text" } }]);
  const updRow = (i, r) => setMedicines(medicines.map((m, idx) => (idx === i ? r : m)));
  const dupRow = (i) => setMedicines([...medicines.slice(0, i + 1), { ...medicines[i], _uid: uid() }, ...medicines.slice(i + 1)]);
  const delRow = (i) => {
    const removed = medicines[i]; // PROV: observational removal event
    if (removed?.brand_name) { const o = originOfRow(removed); provLog({ event_type: "MED_REMOVED", session_id: provSessionRef.current, patient_internal_id: pid, medicine: medEventFields(removed), medicine_origin: o.origin, origin_detail: o.detail, physician_action: "MEDICINE_REMOVED" }); }
    setMedicines(medicines.filter((_, idx) => idx !== i));
  };

  // Sticky brand-tier — when the doctor has previously picked "My Choice" /
  // "Economical" / "Premium", ANY new row added via typeahead auto-swaps to
  // the chosen tier so they don't have to re-tap the tier button on every Rx.
  // Only rows that came from the medicine database (medicine_id set) AND
  // haven't been swapped yet (_origBrand not yet cached) are touched.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const tier = localStorage.getItem("brand_tier_mode");
    if (!tier || tier === "original") return;
    const targets = medicines.filter((m) => m?.brand_name && m?.medicine_id && !m?._origBrand && m?.type !== "insulin" && !m?._fdcCombined);
    if (!targets.length) return;
    if (tier === "mychoice") {
      const pref = (CLINIC.preferred_companies && CLINIC.preferred_companies.length)
        ? CLINIC.preferred_companies
        : ["Eris","Lupin","Mankind","Glenmark","Dr Reddy's","Cipla","GDP Life Sciences","Renwik","Macleods"];
      const next = switchToMyChoice(medicines, pref, pickMyChoiceBrand);
      // Only setState if something actually changed to avoid loops.
      if (next.some((n, i) => n?.brand_name !== medicines[i]?.brand_name)) {
        setMedicines(next);
      }
    } else if (tier === "economical" || tier === "premium") {
      const next = switchBrandTier(medicines, tier);
      if (next.some((n, i) => n?.brand_name !== medicines[i]?.brand_name)) {
        setMedicines(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicines]);

  const addInv = () => setInvestigations([...investigations, emptyInv()]);

  // ── Check-before-add prescribing guard (renal / age / duplicates / allergy / condition / pregnancy / QT) ──
  const [pendingAdds, setPendingAdds] = useState([]);
  const appendNextInv = (lab) => setNextInv((prev) => {
    const trimmed = (prev || "").trim();
    if (trimmed.split(/[,\n]/).map((s) => s.trim().toLowerCase()).includes(lab.trim().toLowerCase())) return prev;
    return trimmed ? `${trimmed}, ${lab}` : lab;
  });
  // Refine My Choice brands against the doctor's OWN 27,797-drug list —
  // real stock + live prices. Silent no-op on failure (static pick stays).
  const refineMyChoiceFromList = async (rows) => {
    try {
      const payload = (rows || [])
        .filter((m) => m?.brand_name && m?.type !== "insulin" && !m?._fdcCombined && m?._uid)
        .map((m) => ({ uid: m._uid, generic_name: m.generic_name, composition: m.composition, strength: m.strength }));
      if (!payload.length) return;
      const pref = (CLINIC.preferred_companies && CLINIC.preferred_companies.length)
        ? CLINIC.preferred_companies
        : ["Eris", "Lupin", "Mankind", "Glenmark", "Dr Reddy's", "Cipla", "GDP Life Sciences", "Renwik", "Macleods"];
      const r = await api.post("/mydrugs/mychoice", { rows: payload, companies: pref });
      const byUid = {};
      (r.data.results || []).forEach((x) => { if (x.matched) byUid[x.uid] = x; });
      const n = Object.keys(byUid).length;
      if (!n) return;
      setMedicines((prev) => prev.map((m) => {
        const hit = byUid[m._uid];
        if (!hit) return m;
        return {
          ...m,
          _origBrand: m._origBrand ?? m.brand_name,
          brand_name: hit.brand,
          _myChoiceCompany: hit.company,
          _myChoicePack: hit.pack,
          _myChoicePrice: hit.price,
          medicine_id: hit.medicine_id || m.medicine_id,
          composition: hit.composition || m.composition,
        };
      }));
      toast.success(`${n} brand${n > 1 ? "s" : ""} matched from My List with pack & price`, { duration: 4000 });
    } catch (e) { console.debug("[silent]", e); }
  };

  // "Cheapest Rx" — swap every row to the cheapest My List equivalent at once
  const cheapestAllRx = async () => {
    try {
      const payload = medicines
        .filter((m) => m?.brand_name && m?._uid && m?.type !== "insulin" && !m?._fdcCombined)
        .map((m) => ({ uid: m._uid, generic_name: m.generic_name, composition: m.composition, strength: m.strength, dosage_form: m.dosage_form, brand_name: m.brand_name, quantity: m.quantity }));
      if (!payload.length) { toast.info("Add medicines first"); return; }
      const r = await api.post("/mydrugs/cheapest-all", { rows: payload });
      const byUid = {};
      (r.data.results || []).forEach((x) => { if (x.matched) byUid[x.uid] = x; });
      const swapped = medicines.filter((m) => byUid[m._uid] && byUid[m._uid].brand !== m.brand_name).length;
      if (!swapped) { toast.info("Rx is already at the cheapest brands in My List"); return; }
      setMedicines((prev) => prev.map((m) => {
        const hit = byUid[m._uid];
        if (!hit || hit.brand === m.brand_name) return m;
        return {
          ...m,
          _origBrand: m._origBrand ?? m.brand_name,
          brand_name: hit.brand,
          medicine_id: hit.medicine_id || m.medicine_id,
          composition: hit.composition || m.composition,
          _myChoiceCompany: hit.manufacturer || "My List",
          _myChoicePack: hit.pack,
          _myChoicePrice: hit.price,
        };
      }));
      const saved = r.data.total_saved || 0;
      toast.success(
        `Cheapest Rx: swapped ${swapped} brand${swapped > 1 ? "s" : ""}${saved > 0 ? ` · saves ₹${saved.toFixed(0)} for this patient` : ""}`,
        { duration: 6000 },
      );
    } catch (e) { console.debug("[silent]", e); toast.error("Cheapest Rx lookup failed"); }
  };

  const doAddMolecule = (row) => {
    let final = row;
    const pref = (CLINIC.preferred_companies && CLINIC.preferred_companies.length)
      ? CLINIC.preferred_companies
      : ["Eris", "Lupin", "Mankind", "Glenmark", "Dr Reddy's", "Cipla", "GDP Life Sciences", "Renwik", "Macleods"];
    const hit = pickMyChoiceBrand(row, pref);
    if (hit && hit.brand !== row.brand_name) {
      final = { ...row, _origBrand: row.brand_name, _myChoiceCompany: hit.company, brand_name: hit.brand };
      toast.success(`My Choice brand: ${hit.brand} (${hit.company})`, { duration: 4000 });
    }    // #7: apply the doctor's remembered dose style, then snapshot card defaults
    const prefRes = applyDosePref(final);
    if (prefRes.applied) {
      final = prefRes.rx;
      toast.info(`Using your usual: ${prefRes.applied}`, { duration: 4000 });
    }
    final = { ...final, _cardDose: final.dose, _cardFreq: final.frequency };
    // Auto-quantity: dose/day × days (duration on the card, else the follow-up period)
    if (!final.quantity) {
      const qty = computeQuantity(final, followUpDays);
      if (qty) final = { ...final, quantity: qty };
    }
    // Materialise a printable dose for one-tap card rows (placeholder-only rows
    // otherwise save dose:null and print an empty Dose cell).
    if (!final.dose && final.strength) final = { ...final, dose: final.strength };
    const withUid = { _uid: uid(), ...final };
    setMedicines((prev) => [...prev.filter((m) => m.brand_name), withUid]);
    refineMyChoiceFromList([withUid]);
    const plans = monitoringPlanFor(row);
    if (plans.length) {
      plans.forEach(appendNextInv);
      toast.info(`Monitoring plan added to Next Investigations: ${plans.join(" · ")}`, { duration: 6000 });
    }
    // PROV: observational — origin captured at the moment the medicine is appended
    const po = originOfRow(withUid);
    provLog({ event_type: "MED_ADDED", session_id: provSessionRef.current, patient_internal_id: pid, medicine: medEventFields(withUid), medicine_origin: po.origin, origin_detail: po.detail, recommendation_id: withUid._prov?.recommendation_id || null, guidance_session_id: withUid._prov?.guidance_session_id || null, gemini_request_id: withUid._prov?.gemini_request_id || null, monitoring_added: plans.length ? plans : null, mechanism: plans.length ? "DETERMINISTIC" : null });
  };
  const guardedAddMolecule = (row) => {
    const issues = checkBeforeAdd(row, guardCtx);
    provGuardIssues(row, issues, "guarded_add"); // PROV: observational only
    const blocks = issues.filter((i) => i.severity === "block");
    if (blocks.length) { setPendingAdds((q) => [...q, { row, issues }]); return false; }
    issues.forEach((i) => toast.warning(i.text, { duration: 7000 }));
    doAddMolecule(row);
    return true;
  };
  // Same guard for the Rx-table typeahead: the row is already filled in place,
  // so a block opens the dialog in "row" mode (Cancel clears, Override keeps).
  const guardRowPick = (idx, filledRow) => {
    // PROV: typeahead selection — origin captured at the moment of selection
    provLog({ event_type: "MED_ADDED", session_id: provSessionRef.current, patient_internal_id: pid, medicine: medEventFields(filledRow), medicine_origin: "PHYSICIAN", origin_detail: "manual_pick" });
    const issues = checkBeforeAdd(filledRow, { ...guardCtx, medicines: medicines.filter((_, j) => j !== idx) });
    provGuardIssues(filledRow, issues, "row_pick"); // PROV: observational only
    const blocks = issues.filter((i) => i.severity === "block");
    if (blocks.length) { setPendingAdds((q) => [...q, { row: filledRow, issues, mode: "row", rowIndex: idx }]); return; }
    issues.forEach((i) => toast.warning(i.text, { duration: 7000 }));
  };

  // Last visit's vitals (newest visit that has any) — powers the ghost hints,
  // height auto-carry, the grey "last recorded" strip and Recall-last-vitals.
  const lastVitalsVisit = useMemo(
    () => prevVisits.find((x) => x.vitals && Object.values(x.vitals).some(Boolean)),
    [prevVisits]
  );
  const lastVitals = lastVitalsVisit?.vitals || {};
  const VITAL_KEYS = ["weight", "height", "waist", "sbp", "dbp", "pulse", "spo2", "temperature", "random_sugar", "fasting_sugar", "pp_sugar"];
  const ghost = (k) => (lastVitals[k] ? `last: ${lastVitals[k]}` : undefined);
  const recallLastVitals = () => {
    const toFill = VITAL_KEYS.filter((k) => !vitals[k] && lastVitals[k]);
    if (!toFill.length) {
      toast.info(Object.keys(lastVitals).length ? "All vitals fields are already filled" : "No vitals found in previous visits");
      return;
    }
    setVitals({ ...vitals, ...Object.fromEntries(toFill.map((k) => [k, lastVitals[k]])) });
    toast.success(`Recalled ${toFill.length} vital(s) from last visit — please re-check before printing`);
  };

  // Recall the most recent saved value of every test from the patient's lab
  // history (original dates preserved). Merge-safe: typed rows stay untouched.
  const [recalling, setRecalling] = useState(false);
  const recallLastLabs = async () => {
    setRecalling(true);
    try {
      const r = await api.get(`/patients/${pid}/latest`);
      const hist = r.data.investigations || [];
      const seen = new Set();
      const latestRows = [];
      hist.forEach((h) => {
        if (!h.test_name || h.result === "" || h.result === null || h.result === undefined) return;
        const key = canonicalKey(h.test_name) || h.test_name.toLowerCase().trim();
        if (seen.has(key)) return;
        seen.add(key);
        latestRows.push(h);
      });
      const kept = investigations.filter((x) => x.test_name);
      const existingKeys = new Set(kept.map((x) => canonicalKey(x.test_name) || x.test_name.toLowerCase().trim()));
      const additions = latestRows
        .filter((h) => !existingKeys.has(canonicalKey(h.test_name) || h.test_name.toLowerCase().trim()))
        .map((h) => ({
          _uid: uid(),
          test_name: h.test_name,
          result: String(h.result ?? ""),
          unit: h.unit || "",
          reference_range: h.reference_range || "",
          test_date: (h.test_date || "").slice(0, 10),
        }));
      if (!additions.length) {
        toast.info(latestRows.length ? "All previous labs are already in the table" : "No previous lab values found for this patient");
      } else {
        setInvestigations([...kept, ...additions]);
        toast.success(`Recalled ${additions.length} lab value(s) — original dates kept`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not recall labs");
    }
    setRecalling(false);
  };
  // Functional update — batches all changes into a single setState so
  // multiple field updates in the same event (test_name + unit + ref) do
  // not overwrite each other via stale closures.
  const updInv = (i, k, v) => setInvestigations((prev) => prev.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const updInvMulti = (i, patch) => setInvestigations((prev) => prev.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const delInv = (i) => setInvestigations(investigations.filter((_, idx) => idx !== i));

  // "Add" chip next to the last visit's `next_investigations` line — appends
  // any comma/newline-separated tests to today's Investigations table, skipping
  // duplicates that the doctor has already added.
  const copyNextTestsToPlan = (nextTestsText) => {
    const items = String(nextTestsText || "")
      .split(/[,\n;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!items.length) { toast.info("No tests to copy"); return; }
    const existingLower = new Set(
      investigations.map((x) => (x.test_name || "").trim().toLowerCase()).filter(Boolean),
    );
    const additions = items
      .filter((t) => !existingLower.has(t.toLowerCase()))
      .map((t) => {
        const meta = LAB_LOOKUP[t] || {};
        return { _uid: uid(), test_name: t, result: "", unit: meta.unit || "", reference_range: meta.reference_range || "", test_date: "" };
      });
    if (!additions.length) { toast.info("Those tests are already on today's plan"); return; }
    // Drop any trailing empty row before appending.
    setInvestigations((prev) => {
      const cleaned = prev.filter((x) => x.test_name?.trim());
      return [...cleaned, ...additions, emptyInv()];
    });
    toast.success(`Added ${additions.length} test${additions.length === 1 ? "" : "s"} to today's plan`);
  };

  const addExtractedInvs = (extracted) => {
    // Merge extracted rows into Investigations with "latest wins" per canonical
    // test name — both across incoming rows AND against rows already in the
    // table. If the user has already typed a value for a canonical test and the
    // report's date is newer, we replace it; otherwise we keep the manual one.
    const dateOf = (r) => (r?.test_date || r?.report_date || "").trim();
    const existing = investigations.filter((x) => x.test_name);
    const byKey = new Map(); // canonical_key -> row
    const emitOrder = []; // preserves insertion order

    const upsert = (row) => {
      const key = canonicalKey(row.test_name) || (row.test_name || "").toLowerCase().trim();
      if (!key) return;
      const prior = byKey.get(key);
      if (!prior) {
        byKey.set(key, row);
        emitOrder.push(key);
        return;
      }
      const nd = dateOf(row);
      const od = dateOf(prior);
      if (nd && nd > od) byKey.set(key, row);
    };

    existing.forEach(upsert);
    (extracted || []).forEach((x) => upsert({
      _uid: uid(),
      test_name: x.test_name || "",
      result: x.result || "",
      unit: x.unit || "",
      reference_range: x.reference_range || "",
      test_date: x.test_date || x.report_date || "",
    }));

    const merged = emitOrder.map((k) => {
      const r = byKey.get(k);
      return r._uid ? r : { _uid: uid(), ...r };
    });
    setInvestigations(merged);
  };

  // Silent auto-capture: free-typed medicines (no medicine_id = never picked
  // from a dropdown) get added to My List so they are searchable next time.
  const autoCaptureNewMeds = (list) => {
    const fresh = list.filter((m) => m.brand_name && !m.medicine_id && m.type !== "insulin");
    if (!fresh.length) return;
    // eslint-disable-next-line no-unused-vars
    api.post("/mydrugs/auto-capture", { medicines: fresh.map(({ _uid, _prov, ...r }) => r), patient_id: pid })
      .then((r) => {
        const a = r.data?.added || [];
        if (a.length) {
          toast.success(
            `${a.length} new medicine${a.length > 1 ? "s" : ""} added to My List: ${a.map((x) => x.brand_name).join(", ")}`,
            { duration: 6000 },
          );
        }
      })
      .catch((e) => console.debug("[silent]", e));
  };

  // PROV: final authorization snapshot — fire-and-forget, fail-open (spec §7)
  const provFinalSnapshot = (rxId, vid, editMode) => {
    try {
      const medsFinal = medicines.filter((m) => m.brand_name).map((m) => {
        const o = originOfRow(m);
        return {
          ...medEventFields(m), timing: m.timing || "", quantity: m.quantity || "", instructions: m.instructions || "",
          medicine_origin: o.origin, origin_detail: o.detail,
          recommendation_id: m._prov?.recommendation_id || null,
          guidance_session_id: m._prov?.guidance_session_id || null,
          gemini_request_id: m._prov?.gemini_request_id || null,
        };
      });
      flushProv();
      provSnapshot({
        prescription_id: rxId, visit_id: vid, patient_internal_id: pid, session_id: provSessionRef.current,
        signed_by_doctor_id: signedByDoctorId || null,
        diagnosis, medicines: medsFinal,
        active_alerts: dAlerts.map((a) => ({ level: a.level, text: a.msg || a.text || "", rule_id: a.rule_id || null, category: a._cat || null })),
        safety_overrides: safetyOverrides, override_reason: overrideAlerts ? overrideReason : "",
        follow_up: followUpDays, next_investigations: nextInv,
        vaccines_planned: (vaccinesPlanned || []).filter((v2) => v2.name && v2.date),
        education_ids: educationIds || [], edit_mode: !!editMode,
      });
    } catch (e) { console.debug("[prov-fail-open]", e?.message); }
  };

  const saveAll = async () => {
    if (!diagnosis.primary) { toast.error("Primary diagnosis required"); return; }
    if (medicines.filter((m) => m.brand_name).length === 0) { toast.error("Add at least one medicine"); return; }
    if (dAlerts.some((a) => a.level === "critical") && (!overrideAlerts || !overrideReason)) {
      toast.error("Critical drug alerts — tick override and enter reason to proceed");
      return;
    }
    setSaving(true);
    // MT-11B.7: separate product strength from patient dose on every row and
    // block unitless doses / unconfirmed tablet splits BEFORE anything is
    // written (the server enforces the same rules).
    // MT-11B.8: RED blocks; AMBER blocks until explicitly confirmed on the row;
    // a combined search-group label is never a prescribable product.
    const verdicts = medicines.filter((m) => m.brand_name).map((m) => ({ uid: m._uid, ...rowQuality(m) }));
    const firstRed = verdicts.flatMap((v) => v.red)[0];
    const firstAmber = verdicts.flatMap((v) => v.pending)[0];
    if (firstRed || firstAmber) {
      toast.error(firstRed ? `Cannot finalise — ${firstRed.message}` : `Needs your confirmation — ${firstAmber.message}`, { duration: 8000 });
      setSaving(false);
      return;
    }
    const savedMeds = verdicts.map((v) => v.normalized);
    setMedicines((prev) => prev.map((m) => savedMeds.find((n) => n._uid && n._uid === m._uid) || m));
    // MT-11B.7: an AI Next Steps follow-up suggestion that the doctor did NOT
    // apply must never reach the saved visit as a date — keep only its reason.
    const nextStepsToSave = (() => {
      if (!nextSteps) return undefined;
      const nf = nextSteps.next_followup;
      if (!nf || !nf.in_days) return nextSteps;
      const applied = `${nf.in_days} days` === String(followUpDays || "").trim();
      return applied
        ? { ...nextSteps, next_followup: { ...nf, applied: true } }
        : { ...nextSteps, next_followup: nf.reason ? { reason: nf.reason, applied: false } : undefined };
    })();
    recordDosePrefs(medicines);
    try {
      // create visit
      // Strip internal FDC/brand-tier bookkeeping fields before saving so
      // the DB stays clean and re-opening the visit doesn't try to "restore".
      const cleanMeds = (list) => list.map((m) => {
        // eslint-disable-next-line no-unused-vars
        const { _uid, _prov, _origBrand, _fdcCombined, _fdcKey, _fdcParts, _myChoiceCompany, _myChoicePack, _myChoicePrice, _cardDose, _cardFreq, ...rest } = m;
        return rest;
      });
      // Compress & Cite: if the doctor left the Summary tab active, print the
      // compressed summary but persist the detailed reasoning for audit.
      const printAdvice = (adviceView === "summary" && adviceSummary.trim()) ? adviceSummary : advice;
      const auditAdvice = advice && advice !== printAdvice ? advice : undefined;

      // -------- EDIT MODE: update the SAME visit + prescription in place --------
      if (editTarget) {
        await api.put(`/visits/${editTarget.visit_id}`, {
          chief_complaints: chief,
          clinical_history: history,
          vitals: { ...vitals, bmi },
          known_case_of: known,
          complications,
          allergy_flags: allergyFlags,
          patient_flags: patientFlags,
          allergy_text: allergyText,
          sjs_culprit: sjsCulprit,
          // eslint-disable-next-line no-unused-vars
          investigations: investigations.filter((i) => i.test_name).map(({ _uid, ...rest }) => rest),
          diagnosis,
          medicines: cleanMeds(savedMeds),
          advice: printAdvice,
          advice_detailed: auditAdvice,
          follow_up: followUpDays,
          next_investigations: nextInv,
          imaging_reviewed: imagingReviewed.length ? imagingReviewed : undefined,
          vaccines_planned: vaccinesPlanned && vaccinesPlanned.length ? vaccinesPlanned.filter((v) => v.name && v.date) : undefined,
        });
        let rxId = editTarget.prescription_id;
        const rxBody = {
          medicines: cleanMeds(savedMeds),
          diagnosis,
          advice: printAdvice,
          advice_detailed: auditAdvice,
          follow_up: followUpDays,
          vaccines_planned: vaccinesPlanned && vaccinesPlanned.length ? vaccinesPlanned.filter((v) => v.name && v.date) : undefined,
          override_reason: overrideAlerts ? overrideReason : "",
          safety_overrides: safetyOverrides.length ? safetyOverrides : undefined,
          safety_reviewed: true,
          signed_by_doctor_id: signedByDoctorId || undefined,
        };
        if (rxId) {
          await api.put(`/prescriptions/${rxId}`, rxBody);
        } else {
          const rx = await api.post("/prescriptions", { ...rxBody, patient_id: pid, visit_id: editTarget.visit_id });
          rxId = rx.data.prescription.prescription_id;
        }
        autoCaptureNewMeds(medicines);
        provFinalSnapshot(rxId, editTarget.visit_id, true); // PROV
        toast.success("Visit & prescription updated");
        clearDraft();
        setSaving(false);
        nav(`/print/${rxId}`);
        return;
      }

      const vres = await api.post("/visits", {
        patient_id: pid,
        visit_type: "New",
        chief_complaints: chief,
        clinical_history: history,
        vitals: { ...vitals, bmi },
        known_case_of: known,
        complications,
        allergy_flags: allergyFlags,
        patient_flags: patientFlags,
        allergy_text: allergyText,
        sjs_culprit: sjsCulprit,
        // eslint-disable-next-line no-unused-vars
        investigations: investigations.filter((i) => i.test_name).map(({ _uid, ...rest }) => rest),
        diagnosis,
        // Persist medicines on the visit too so Ct-all clone and visit history
        // lists show them correctly. Prescription is stored separately below.
        medicines: cleanMeds(savedMeds),
        advice: printAdvice,
        advice_detailed: auditAdvice,
        follow_up: followUpDays,
        next_investigations: nextInv,
        next_steps: nextStepsToSave,
        imaging_reviewed: imagingReviewed.length ? imagingReviewed : undefined,
        education_ids: educationIds && educationIds.length ? educationIds : undefined,
        vaccines_planned: vaccinesPlanned && vaccinesPlanned.length ? vaccinesPlanned.filter((v) => v.name && v.date) : undefined,
        // Snapshot the calculator pins onto the visit so the printed Rx keeps
        // them even when re-printed later or from a different device (this
        // used to live only in localStorage and was lost on regeneration).
        pinned_calculators: (loadPinnedCalcs() || []).filter((p) => p && p.value !== null && p.value !== undefined && p.value !== ""),
        continuation: continuationNote || undefined,
      });
      const vid = vres.data.visit.visit_id;
      // save prescription
      const rx = await api.post("/prescriptions", {
        patient_id: pid,
        visit_id: vid,
        medicines: cleanMeds(savedMeds),
        diagnosis,
        advice: printAdvice,
        advice_detailed: auditAdvice,
        follow_up: followUpDays,
        vaccines_planned: vaccinesPlanned && vaccinesPlanned.length ? vaccinesPlanned.filter((v) => v.name && v.date) : undefined,
        override_reason: overrideAlerts ? overrideReason : "",
        safety_overrides: safetyOverrides.length ? safetyOverrides : undefined,
        safety_reviewed: true,
        signed_by_doctor_id: signedByDoctorId || undefined,
      });
      autoCaptureNewMeds(medicines);
      provFinalSnapshot(rx.data.prescription.prescription_id, vid, false); // PROV
      toast.success("Visit & prescription saved");
      clearDraft();
      setLastVisitId(vid);
      nav(`/print/${rx.data.prescription.prescription_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  // Generate the whole Advice + Follow-up block in one click. Merges into
  // the Detailed tab (never blows away doctor's manual work) and pre-fills
  // follow-up interval + next-investigations. Doctor edits before saving.
  const [generatingAdvice, setGeneratingAdvice] = useState(false);
  const [adviceLang, setAdviceLang] = useState("en");
  useEffect(() => { setAdviceLang(patient?.preferred_language || "en"); }, [pid, patient?.preferred_language]);
  const generateAdviceFollowup = async () => {
    setGeneratingAdvice(true);
    try {
      // Flatten client-side state shapes to what the backend endpoint expects:
      // diagnosis is an object here but a list of {condition} on the wire;
      // history is an object here but a plain string on the wire.
      const dxList = [];
      if (diagnosis?.primary) dxList.push({ condition: diagnosis.primary });
      if (diagnosis?.secondary) dxList.push({ condition: diagnosis.secondary });
      const historyStr = [
        history?.hpi && `HPI: ${history.hpi}`,
        history?.past_medical && `Past medical: ${history.past_medical}`,
        history?.drug_hx && `Drug hx: ${history.drug_hx}`,
        history?.family_hx && `Family hx: ${history.family_hx}`,
        history?.personal_hx && `Personal hx: ${history.personal_hx}`,
        history?.notes && `Notes: ${history.notes}`,
      ].filter(Boolean).join("\n");
      const r = await api.post("/ai/generate-advice-followup", {
        patient_id: pid,
        lang: adviceLang,
        context: {
          age: patient?.age,
          sex: patient?.sex,
          chief,
          history: historyStr,
          known, complications,
          allergy_text: allergyText,
          diagnosis: dxList,
          medicines: medicines.filter((m) => m.brand_name),
          vitals: { ...vitals, bmi },
          investigations: investigations.filter((i) => i.test_name),
        },
      });
      const { advice: adv, follow_up: fu, next_investigations: ni, red_flags: rf } = r.data || {};
      // Compose: advice + a red-flags line block if present. Preserve any
      // existing doctor-typed advice by appending, not replacing.
      const block = [adv || "", rf ? `\nSeek help immediately if:\n${rf}` : ""].filter(Boolean).join("\n");
      if (block) {
        setAdvice((prev) => {
          const cur = (prev || "").trim();
          return cur ? `${cur}\n\n${block}` : block;
        });
        setAdviceView("detailed");
        setAdviceSummary("");
      }
      if (fu) setFollowUpDays(fu);
      if (ni) {
        setNextInv((prev) => {
          const cur = (prev || "").trim();
          if (!cur) return ni;
          // Merge without dup — split, uniq (case-insensitive), rejoin.
          const seen = new Set(cur.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean));
          const extras = ni.split(",").map((s) => s.trim()).filter((s) => s && !seen.has(s.toLowerCase()));
          return extras.length ? `${cur}, ${extras.join(", ")}` : cur;
        });
      }
      toast.success("Advice, follow-up & next-labs generated — review before saving");
    } catch (e) {
      console.error("generateAdviceFollowup failed:", e);
      toast.error(e.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setGeneratingAdvice(false);
    }
  };

  const compressAdvice = async () => {
    if (!advice) { toast.warning("Nothing to compress — Advice is empty"); return; }
    setCompressing(true);
    try {
      const r = await api.post("/ai/compress-advice", {
        text: advice,
        style: adviceStyle,     // 'prose' | 'bullets'
        lang: "en",             // print language is English; keep numbers/drug names intact
        patient_id: pid,
      });
      const summary = (r.data?.summary || "").trim();
      if (!summary) { toast.error("AI returned an empty summary — try again"); return; }
      setAdviceSummary(summary);
      setAdviceView("summary");
      toast.success("Summary ready — detailed reasoning kept for audit");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compress & Cite failed");
    } finally {
      setCompressing(false);
    }
  };

  const uploadReport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("patient_id", pid);
    fd.append("category", "Laboratory Reports");
    fd.append("auto_extract", "1");
    try {
      const r = await api.post("/reports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const rep = r.data.report;
      setReports([rep, ...reports]);
      if (rep.extraction_status === "pending") {
        toast.info("Report uploaded — AI is extracting lab values in the background…");
        pollReportExtraction(rep.report_id);
      } else if (rep.extraction_status === "ok" && (rep.extracted_tests || []).length) {
        addExtractedInvs(rep.extracted_tests);
        toast.success(`Auto-loaded ${rep.extracted_tests.length} lab values from report`);
      } else {
        toast.success("Report uploaded");
      }
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  // Poll a report's extraction status until ok/failed, then merge into
  // Investigations. Backs off gracefully; auto-cancels after ~4 minutes.
  const pollReportExtraction = React.useCallback(async (reportId) => {
    const started = Date.now();
    const maxMs = 4 * 60 * 1000;
    while (Date.now() - started < maxMs) {
      await new Promise((res) => setTimeout(res, 5000));
      try {
        const s = await api.get(`/reports/${reportId}/status`);
        const status = s.data?.extraction_status;
        if (status === "ok") {
          const tests = s.data.extracted_tests || [];
          if (tests.length) {
            addExtractedInvs(tests);
            toast.success(`Auto-loaded ${tests.length} lab values from ${s.data.laboratory || "report"} (${s.data.report_date || "date n/a"})`);
          } else {
            toast.warning("Report extracted but no structured lab values found");
          }
          setReports((prev) => prev.map((rp) => rp.report_id === reportId ? { ...rp, ...s.data } : rp));
          return;
        }
        if (status === "failed") {
          toast.error("AI extraction failed — you can retry or enter labs manually");
          return;
        }
      } catch (_) { /* keep polling */ }
    }
    toast.warning("Extraction is still running in the background — check back in a minute");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const extractAI = async (report_id) => {
    // If the background job already produced extracted_tests, just merge — no
    // need to re-hit Gemini. Fall back to the on-demand endpoint otherwise
    // (older reports uploaded before the auto-extract feature landed).
    const cached = reports.find((r) => r.report_id === report_id);
    // Manual entries: same merge path, but different toast copy so the
    // doctor knows the values came from a human, not the AI.
    if (cached?.entry_mode === "manual" || cached?.extraction_status === "manual") {
      const rows = (cached.extracted_tests || []).map((x) => ({ ...x, report_date: cached.report_date || "" }));
      if (!rows.length) { toast.warning("This manual entry has no test rows"); return; }
      addExtractedInvs(rows);
      toast.success(`Merged ${rows.length} manually-entered lab value${rows.length > 1 ? "s" : ""}`);
      return;
    }
    if (cached?.extraction_status === "ok" && (cached.extracted_tests || []).length) {
      const rows = cached.extracted_tests.map((x) => ({ ...x, report_date: cached.report_date || "" }));
      addExtractedInvs(rows);
      toast.success(`Auto-loaded ${rows.length} lab values`);
      return;
    }
    if (cached?.extraction_status === "pending") {
      toast.info("Extraction still running — will auto-populate when ready");
      pollReportExtraction(report_id);
      return;
    }
    toast.info("Extracting values with AI…");
    try {
      const r = await api.post("/ai/extract-report", { report_id });
      const t = r.data.extracted?.tests || [];
      if (t.length) {
        const rows = t.map((x) => ({ test_name: x.test_name || "", result: x.result || "", unit: x.unit || "", reference_range: x.reference_range || "", test_date: r.data.extracted?.report_date || "" }));
        addExtractedInvs(rows);
        toast.success(`Extracted ${rows.length} values — verify them`);
      } else {
        toast.warning("No structured values detected. Enter manually.");
      }
    } catch (e) { console.warn("AI extraction failed:", e); toast.error("AI extraction failed"); }
  };

  if (!patient) return <Layout><div className="p-6 text-slate-500">Loading…</div></Layout>;

  return (
    <Layout contentClassName="patient-workspace">
      <Toaster position="top-right" richColors />
      {/* Sticky clinical flow rail — jumps to any section in the visit form.
          Hidden on tablets/phones (< lg) to preserve real estate. */}
      <div className="hidden lg:block fixed right-3 top-24 z-30 pointer-events-none">
        <div className="pointer-events-auto">
          <VisitFlowRail
            steps={[
              { id: "section-vitals", label: "Vitals", filled: !!(vitals.weight || vitals.sbp || vitals.pulse || vitals.random_sugar) },
              { id: "section-history", label: "Complaints", filled: !!(chief || history.hpi) },
              { id: "section-known", label: "Known case", filled: (known || []).length > 0 },
              { id: "section-diagnosis", label: "Diagnosis", filled: !!diagnosis.primary },
              { id: "section-investigations", label: "Investigations", filled: investigations.some((i) => i.test_name) },
              { id: "prescription-panel", label: "Prescription", filled: medicines.some((m) => m.brand_name) },
              { id: "section-advice", label: "Advice", filled: !!(advice || adviceSummary) },
              { id: "section-vaccinations", label: "Vaccinations" },
              { id: "section-education", label: "Education", filled: (educationIds || []).length > 0 },
            ]}
          />
        </div>
      </div>
      {/* Reserve right-side space on lg+ so the fixed VisitFlowRail never
          overlaps interactive controls in the main column (e.g. the
          medicine-row delete X, copy and up/down buttons on the far right
          of the Prescription table). */}
      <div className="lg:pr-[200px] xl:pr-[220px]">
      <DraftBanner savedAt={draftSavedAt} onDiscard={() => { clearDraft(); setDraftSavedAt(null); toast.info("Draft discarded"); }} />
      {clonedFrom && (
        <div className="mb-3 border border-emerald-200 bg-emerald-50 rounded-md p-3 text-sm flex items-start justify-between gap-3" data-testid="clone-banner">
          <div className="flex items-start gap-2 text-emerald-900">
            <Copy size={14} className="mt-0.5 shrink-0 text-emerald-700" />
            <div>
              <div className="font-medium">Cloned last prescription</div>
              <div className="text-xs mt-0.5">
                Copied {clonedFrom.medicine_count} medicine{clonedFrom.medicine_count !== 1 ? "s" : ""}
                {clonedFrom.primary ? <> · diagnosis <strong>{clonedFrom.primary}</strong></> : null}
                {" from "}
                <span className="tabular">{new Date(clonedFrom.visit_date).toLocaleDateString()}</span>.
                {" "}Please verify vitals, doses and duration before saving.
              </div>
            </div>
          </div>
          <button onClick={() => setClonedFrom(null)} className="text-[10px] text-emerald-700 hover:text-emerald-900" data-testid="clone-banner-dismiss">Dismiss</button>
        </div>
      )}
      {editTarget && (
        <div className="mb-3 border border-amber-300 bg-amber-50 rounded-md p-3 text-sm flex items-start justify-between gap-3" data-testid="edit-mode-banner">
          <div className="flex items-start gap-2 text-amber-900">
            <FileText size={14} className="mt-0.5 shrink-0 text-amber-700" />
            <div>
              <div className="font-medium">Editing saved visit of {new Date(editTarget.visit_date).toLocaleDateString()}</div>
              <div className="text-xs mt-0.5">
                All vitals, labs and medicines are loaded. Saving will <strong>update the same visit &amp; prescription</strong> — no duplicate is created.
              </div>
            </div>
          </div>
          <button
            onClick={() => { window.location.href = `/patient/${pid}`; }}
            className="text-[10px] text-amber-700 hover:text-amber-900 whitespace-nowrap"
            data-testid="edit-mode-cancel"
          >
            Cancel edit
          </button>
        </div>
      )}
      {pregnancyMode && (
        <div className="mb-3 border border-pink-300 bg-pink-50 rounded-md p-3 text-sm flex items-start gap-2" data-testid="pregnancy-banner">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-pink-700" />
          <div className="text-pink-900">
            <div className="font-medium">Pregnancy — prescribing in maternal-safety mode</div>
            <div className="text-xs mt-0.5">
              Every medicine is screened for pregnancy safety. Statins, ACEi/ARB, oral antidiabetics, unsafe antibiotics and NSAIDs
              will be flagged below the prescription table. Insulin is first-line for glycaemic control (ADA 2026 §15).
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => nav("/dashboard")} className="p-1.5 rounded hover:bg-slate-100" data-testid="back-btn"><ArrowLeft size={16} /></button>
          <div>
            <div className="overline">PATIENT</div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900 flex items-center gap-1.5">
              {patient.name}
              <EditPatientDialog patient={patient} onSaved={(p) => setPatient(p)} />
            </h1>
            <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-3 tabular">
              <span>{patient.patient_id}</span>
              <span>{patient.mobile}</span>
              <span>{patient.age ? `${patient.age} yr` : ""} {patient.sex}</span>
              <span>{prevVisits.length} previous visit(s)</span>
            </div>
            <PatientLanguagePreference key={pid} patient={patient} onSaved={setPatient} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {criticalCount > 0 && (
            <Badge className="alert-critical border" data-testid="critical-count-badge">
              <AlertTriangle size={12} className="mr-1" /> {criticalCount} critical
            </Badge>
          )}
          <div className="flex gap-0.5 border border-slate-200 rounded p-0.5 bg-white" data-testid="layout-toggle">
            {[
              { key: "split", icon: Columns2, title: "Split view" },
              { key: "wide", icon: PanelRight, title: "Wide prescription" },
              { key: "full", icon: Maximize2, title: "Full-width prescription" },
              { key: "compare", icon: GitCompareArrows, title: "Compare with last visit" },
            ].map((m) => {
              const Icon = m.icon;
              const active = layout === m.key;
              return (
                <button
                  key={m.key}
                  title={m.title}
                  onClick={() => { setLayout(m.key); localStorage.setItem("pd-layout", m.key); }}
                  data-testid={`layout-${m.key}`}
                  className={`p-1.5 rounded ${active ? "bg-pink-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                  disabled={m.key === "compare" && prevVisits.length === 0}
                >
                  <Icon size={14} />
                </button>
              );
            })}
          </div>
          {(lastVisitId || prevVisits[0]?.visit_id) && (
            <>
              <ReferralDialog
                patientId={pid}
                visitId={lastVisitId || prevVisits[0].visit_id}
                defaultSummary={[
                  diagnosis.primary && `Diagnosis: ${diagnosis.primary}`,
                  chief && `Chief complaints: ${chief}`,
                  known.length && `Known case of: ${known.join(", ")}`,
                  `Current vitals: BP ${vitals.sbp || "-"}/${vitals.dbp || "-"}, BMI ${bmi || "-"}`,
                ].filter(Boolean).join("\n")}
                trigger={
                  <Button variant="outline" className="gap-2 border-slate-300" data-testid="open-referral-btn">
                    <Send size={14} /> Referral
                  </Button>
                }
              />
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const r = await api.post("/portal/link", { patient_id: pid });
                    const msg = `Dear ${patient.name},\n\nView your latest prescription and reports from ${CLINIC.name} here:\n${r.data.portal_url}\n\nEnter the last 4 digits of this mobile number as OTP. This link expires in 24 hours.\n\n— ${CLINIC.short}`;
                    openWhatsApp(patient.mobile, msg);
                    toast.success("Portal link created — WhatsApp opened");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to create portal link");
                  }
                }}
                className="gap-2 border-slate-300"
                title="Generates a share link and opens WhatsApp so the patient can view their prescription and reports on their phone. They enter the last 4 digits of this mobile as OTP. Link expires in 24 hours."
                data-testid="portal-link-btn"
              >
                <Link2 size={14} /> Portal
              </Button>
            </>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2 border-slate-300" data-testid="open-cert-menu-btn">
                <FileCheck size={14} /> Certificate
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 py-1.5">Issue certificate</div>
              {CERT_LIST.map((c) => (
                <button
                  key={c.key}
                  className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-pink-50 text-slate-800 flex items-center gap-2"
                  onClick={() => nav(`/certificates/new/${pid}/${c.key}`)}
                  data-testid={`cert-menu-${c.key}`}
                >
                  <FileCheck size={13} className="text-pink-500" />
                  {c.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <Button
            variant="outline"
            className="gap-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
            onClick={() => nav(`/diet/${pid}`)}
            data-testid="open-diet-hub-btn"
          >
            <Salad size={14} /> Diet Plan
          </Button>
          <Button
            variant="outline"
            className="gap-2 border-indigo-500 text-indigo-700 hover:bg-indigo-50"
            onClick={() => nav(`/exercise/${pid}`)}
            data-testid="open-exercise-hub-btn"
          >
            <Dumbbell size={14} /> Exercise Plan
          </Button>
          <Button
            variant="outline"
            className="gap-2 border-pink-500 text-pink-700 hover:bg-pink-50"
            onClick={() => nav(`/education?pid=${pid}`)}
            data-testid="open-education-btn"
          >
            <BookOpen size={14} /> Education
          </Button>
          {/* PHASE-B1 prescriber binding: doctors may only sign as their linked
              profile (or an explicit delegate); admins may pick any doctor.
              No silent default to the clinic's first doctor. */}
          {(() => {
            const docs = CLINIC.doctors || [];
            // MT-12B: owners and Support Mode superadmins prescribe ONLY through their own linked doctor profile
            const unlinked = !me?.doctor_id && (me?.role === "doctor" || me?.role === "owner" || !!me?.support_mode);
            if (unlinked) return (
              <span className="inline-flex items-center text-[10px] px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-800" data-testid="prescriber-unlinked-warning" title={me?.support_mode ? "Support Mode: this superadmin is not a linked doctor of this clinic — clinical writes are denied" : "Ask the admin to link your account to a doctor profile in Staff & Roles"}>
                {me?.support_mode ? "Support Mode — no prescribing (not a linked doctor here)" : me?.role === "owner" ? "Owner not linked to a doctor profile — prescribing disabled" : "Prescriber not linked — set in Staff & Roles"}
              </span>
            );
            const authorised = me?.role === "admin" && !me?.support_mode
              ? docs
              : docs.filter((d) => d.id === me?.doctor_id || (!me?.support_mode && (me?.delegate_doctor_ids || []).includes(d.id)));
            if (!authorised.length || (authorised.length <= 1 && docs.length <= 1)) return null;
            return (
              <select
                value={signedByDoctorId}
                onChange={(e) => setSignedByDoctorId(e.target.value)}
                className="h-9 px-2 border border-slate-300 rounded-md text-xs"
                data-testid="save-visit-doctor-picker"
                title="Which doctor signs this prescription?"
              >
                {!signedByDoctorId && <option value="" disabled>Select prescriber…</option>}
                {authorised.map((d) => (
                  <option key={d.id} value={d.id}>Signed by: {d.name}</option>
                ))}
              </select>
            );
          })()}
          <Button variant="outline" onClick={() => setDraftDialogOpen(true)} disabled={savingDraft || saving} data-testid="save-draft-btn" className="gap-2 border-slate-300" title="Save this visit as a cloud draft — labs pending? Resume later from any PC via the Drafts page">
            <FileClock size={14} /> {savingDraft ? "Saving…" : "Draft"}
          </Button>
          <Button onClick={saveAll} disabled={saving} data-testid="save-visit-btn" className="bg-pink-600 hover:bg-pink-700 text-white gap-2">
            <Save size={14} /> {saving ? "Saving…" : "Save & Print"}
          </Button>
        </div>
      </div>

      <div className="mb-6">
        {/* Investigations lives above the two-column grid so its wide table
            never gets squeezed by the sticky Prescription panel on the right. */}
        <Section
          title="Investigations"
          testid="section-investigations"
          filled={investigations.some((i) => i.test_name)}
          summary={investigations.filter((i) => i.test_name).length ? `${investigations.filter((i) => i.test_name).length} test${investigations.filter((i) => i.test_name).length === 1 ? "" : "s"}` : ""}
        >
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <LabCatalogPicker
              catalog={LAB_CATALOG}
              onAdd={(name) => {
                const meta = LAB_LOOKUP[name] || {};
                const existing = investigations.filter((x) => x.test_name);
                if (existing.some((x) => x.test_name?.toLowerCase() === name.toLowerCase())) {
                  toast.info(`${name} already added`);
                  return;
                }
                const row = { test_name: name, result: "", unit: meta.unit || "", reference_range: meta.ref || "", test_date: new Date().toISOString().slice(0, 10) };
                setInvestigations([...existing, { _uid: uid(), ...row }]);
              }}
            />
            <LabPackagePicker
              packages={LAB_PACKAGES}
              onAdd={(pkg) => {
                const existing = investigations.filter((x) => x.test_name);
                const existingNames = new Set(existing.map((x) => x.test_name?.toLowerCase()));
                const added = [];
                pkg.tests.forEach((t) => {
                  if (existingNames.has(t.toLowerCase())) return;
                  const meta = LAB_LOOKUP[t] || {};
                  added.push({ test_name: t, result: "", unit: meta.unit || "", reference_range: meta.ref || "", test_date: new Date().toISOString().slice(0, 10) });
                  existingNames.add(t.toLowerCase());
                });
                if (added.length) {
                  setInvestigations([...existing, ...added.map((x) => ({ _uid: uid(), ...x }))]);
                  toast.success(`Added ${added.length} tests from ${pkg.label}`);
                } else {
                  toast.info("All those tests are already added");
                }
              }}
            />
            <Button
              variant="outline"
              onClick={recallLastLabs}
              disabled={recalling}
              className="gap-1.5 h-8 text-xs border-pink-300 text-pink-700 hover:bg-pink-50"
              title="Refill the table with this patient's most recent saved value for every test (original dates preserved). Anything already typed stays untouched."
              data-testid="recall-last-labs-btn"
            >
              <History size={12} /> {recalling ? "Recalling…" : "Recall last labs"}
            </Button>
            <div className="ml-auto text-[10px] text-slate-500">
              {investigations.filter((x) => x.test_name).length} test(s) · staff can fill values later
            </div>
          </div>
          {Object.values(latestLabs).some((l) => l && l.value !== null && l.value !== undefined) && (() => {
            const items = Object.entries(latestLabs)
              .filter(([k, l]) => l && l.value !== null && l.value !== undefined && l.test_name && !["weight", "bmi"].includes(k))
              .map(([, l]) => l)
              .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
            if (!items.length) return null;
            const newest = items[0].date || "";
            const fmtD = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
            return (
              <div className="mb-2 text-[11px] text-slate-400 leading-relaxed" data-testid="last-labs-strip">
                <span className="uppercase tracking-wider text-[9px] font-semibold">Last labs on file{newest ? ` · ${fmtD(newest)}` : ""}:</span>{" "}
                {items.map((l, i) => {
                  const abn = labAbnormal(l);
                  return (
                    <span key={`${l.test_name}-${i}`}>
                      {i > 0 && " · "}
                      <span className={abn ? "text-red-600 font-semibold" : undefined} data-testid={abn ? `abnormal-lab-${i}` : undefined} title={abn ? `Outside ref range ${l.ref_range || CATALOG_REF[(l.test_name || "").toLowerCase()] || ""}` : undefined}>
                        {l.test_name} {l.value}{l.unit ? ` ${l.unit}` : ""}{abn ? " ⚠" : ""}
                      </span>
                      {l.date && l.date !== newest ? ` (${fmtD(l.date)})` : ""}
                    </span>
                  );
                })}
              </div>
            );
          })()}
          {(() => {
            // Deterministic eGFR trend alert — falling kidney function means
            // renally-cleared drug doses need a fresh look.
            const e = latestLabs.egfr;
            if (!e || e.previous == null || e.value == null) return null;
            const dropAbs = e.previous - e.value;
            const dropPct = e.previous ? (dropAbs / e.previous) * 100 : 0;
            const crossed60 = e.value < 60 && e.previous >= 60;
            const crossed30 = e.value < 30 && e.previous >= 30;
            if (!(dropAbs >= 15 || dropPct >= 20 || crossed60 || crossed30)) return null;
            const sev = crossed30 || e.value < 30 ? "red" : "amber";
            const cls = sev === "red" ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-800";
            const cr = latestLabs.creatinine;
            return (
              <div className={`mb-2 rounded-md border px-3 py-2 text-[11px] leading-snug ${cls}`} data-testid="egfr-trend-alert">
                <span className="font-bold">eGFR falling: {e.previous} → {e.value}</span>
                <span> ({dropPct >= 1 ? `−${dropPct.toFixed(0)}%` : `−${dropAbs.toFixed(0)}`}{e.previous_date ? ` since ${new Date(e.previous_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}` : ""})</span>
                {crossed60 && <span className="font-semibold"> — crossed below 60 (CKD G3a band)</span>}
                {crossed30 && <span className="font-semibold"> — crossed below 30 (G4: stop/avoid metformin, review all renal doses)</span>}
                {cr?.value != null && cr.previous != null && cr.value > cr.previous && <span> · creatinine {cr.previous} → {cr.value}</span>}
                <div className="mt-0.5 text-[10px] opacity-80">Review renally-cleared drugs: metformin, SGLT2i, DOACs, NSAIDs, TZDs, digoxin — the dose banner below uses today's eGFR.</div>
              </div>
            );
          })()}
          <div className="overflow-x-auto">
              <table className="w-full text-sm visit-responsive-table visit-lab-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 min-w-[220px]">Test</th>
                  <th className="min-w-[130px]">Result</th>
                  <th className="w-16">Trend</th>
                  <th className="min-w-[110px]">Unit</th>
                  <th className="min-w-[220px]">Ref Range</th>
                  <th className="min-w-[150px]">Date</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {investigations.map((inv, i) => (
                  <tr key={inv._uid || `inv-${i}`} className="border-b border-slate-100">
                    <td className="py-1 pr-2">
                      <Input
                        list="lab-catalog-list"
                        value={inv.test_name}
                        onChange={(e) => {
                          const v = e.target.value;
                          const meta = LAB_LOOKUP[v];
                          if (meta) {
                            // Batch all three field updates in one setState so
                            // the second/third calls don't clobber test_name.
                            updInvMulti(i, {
                              test_name: v,
                              ...(inv.unit ? {} : { unit: meta.unit || "" }),
                              ...(inv.reference_range ? {} : { reference_range: meta.ref || "" }),
                            });
                          } else {
                            updInv(i, "test_name", v);
                          }
                        }}
                        className="h-8 text-sm"
                        placeholder="e.g. HbA1c (search catalog…)"
                        data-testid={`inv-name-${i}`}
                      />
                    </td>
                    <td className="pr-2"><Input value={inv.result} onChange={(e) => updInv(i, "result", e.target.value)} className="h-8 text-sm tabular w-full" data-testid={`inv-result-${i}`} /></td>
                    <td className="pr-2"><TrendChip testName={inv.test_name} value={inv.result} latestMap={latestLabs} /></td>
                    <td className="pr-2"><Input value={inv.unit} onChange={(e) => updInv(i, "unit", e.target.value)} className="h-8 text-sm w-full" /></td>
                    <td className="pr-2"><Input value={inv.reference_range} onChange={(e) => updInv(i, "reference_range", e.target.value)} className="h-8 text-sm w-full" /></td>
                    <td className="pr-2"><Input type="date" value={inv.test_date} onChange={(e) => updInv(i, "test_date", e.target.value)} className="h-8 text-sm tabular w-full" /></td>
                    <td><button onClick={() => delInv(i)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <datalist id="lab-catalog-list">
              {LAB_CATALOG.flatMap((g) => g.tests).map((t) => (
                <option key={t.name} value={t.name} />
              ))}
            </datalist>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 visit-toolbar">
            <Button variant="outline" size="sm" onClick={addInv} className="gap-1" data-testid="add-investigation-btn"><Plus size={12} /> Add empty row</Button>
            <AiExtractDialog testid="ai-extract-inv" onAdd={addExtractedInvs} latestMap={latestLabs} />
          </div>

          {anaemiaCall && (
            <div className="mt-3 rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2" data-testid="anaemia-classifier-chip">
              <div className="text-[11px] font-semibold text-rose-800 flex items-center gap-2 flex-wrap">
                <span>Anaemia classifier: {anaemiaCall.type}</span>
                <span className="text-[9px] uppercase tracking-wide bg-white border border-rose-300 rounded-full px-1.5 py-0.5">{anaemiaCall.guideline}</span>
              </div>
              <div className="text-[10px] text-rose-700 mt-0.5 tabular" data-testid="anaemia-classifier-detail">{anaemiaCall.detail}</div>
              {anaemiaCall.workup?.length > 0 && (
                <div className="text-[10px] text-slate-600 mt-0.5">Next: {anaemiaCall.workup.join(" · ")}</div>
              )}
              <div className="text-[9px] text-slate-400 mt-0.5">Deterministic aid from entered values — clinical correlation required.</div>
            </div>
          )}

          <LiveCalcStrip
            investigations={investigations}
            age={patient?.age}
            sex={patient?.sex}
            onPushInvestigation={(row) => setInvestigations([...investigations.filter((x) => x.test_name), { _uid: uid(), ...row }])}
          />

          <ClinicalCalculators
            investigations={investigations}
            vitals={{ ...vitals, bmi }}
            age={patient?.age}
            sex={patient?.sex}
            onPushInvestigation={(row) => setInvestigations([...investigations.filter((x) => x.test_name), { _uid: uid(), ...row }])}
            onAppendAdvice={(line) => {
              setAdvice((prev) => {
                const trimmed = (prev || "").trimEnd();
                return trimmed ? `${trimmed}\n${line}` : line;
              });
              toast.success("Copied to Advice / Rx");
            }}
          />
        </Section>
      </div>

      <div className={`grid gap-6 ${layout === "full" ? "md:grid-cols-1" : "md:grid-cols-12"} ${layout === "compare" ? "lg:pr-[380px]" : ""}`}>
        {/* LEFT column – clinical form */}
        <div className={`min-w-0 ${layout === "full" ? "" : layout === "wide" ? "md:col-span-5 space-y-4" : "md:col-span-8 space-y-4"}`}>
          {/* Vitals */}
          <Section
            title="Vitals & Anthropometry"
            testid="section-vitals"
            filled={!!(vitals.weight || vitals.height || vitals.sbp || vitals.dbp || vitals.pulse || vitals.random_sugar)}
            summary={vitals.sbp || vitals.dbp ? `BP ${vitals.sbp || "-"}/${vitals.dbp || "-"}${bmi ? ` · BMI ${bmi}` : ""}` : (bmi ? `BMI ${bmi}` : "")}
          >
            <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
              {lastVitalsVisit ? (
                <div className="text-[11px] text-slate-400 leading-snug flex-1 min-w-[200px]" data-testid="last-vitals-strip">
                  <span className="uppercase tracking-wider text-[9px] font-semibold">Last recorded{lastVitalsVisit.visit_date ? ` · ${new Date(lastVitalsVisit.visit_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : ""}:</span>{" "}
                  {[
                    lastVitals.weight && `Wt ${lastVitals.weight} kg`,
                    lastVitals.height && `Ht ${lastVitals.height} cm`,
                    (lastVitals.sbp || lastVitals.dbp) && `BP ${lastVitals.sbp || "-"}/${lastVitals.dbp || "-"}`,
                    lastVitals.pulse && `Pulse ${lastVitals.pulse}`,
                    lastVitals.spo2 && `SpO₂ ${lastVitals.spo2}%`,
                    lastVitals.temperature && `Temp ${lastVitals.temperature}°C`,
                    lastVitals.waist && `Waist ${lastVitals.waist} cm`,
                    lastVitals.random_sugar && `RBS ${lastVitals.random_sugar}`,
                    lastVitals.fasting_sugar && `FBS ${lastVitals.fasting_sugar}`,
                    lastVitals.pp_sugar && `PPBS ${lastVitals.pp_sugar}`,
                  ].filter(Boolean).join(" · ")}
                </div>
              ) : <div />}
              <Button
                variant="outline"
                onClick={recallLastVitals}
                className="gap-1.5 h-7 text-xs border-pink-300 text-pink-700 hover:bg-pink-50 shrink-0"
                title="Copy last visit's vitals into today's empty fields. Anything you've typed stays untouched — re-check values before printing."
                data-testid="recall-last-vitals-btn"
              >
                <History size={12} /> Recall last vitals
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <F label="WEIGHT (kg)"><Input value={vitals.weight || ""} placeholder={ghost("weight")} onChange={(e) => setVitals({ ...vitals, weight: e.target.value })} className="tabular" data-testid="vitals-weight" /></F>
              <F label="HEIGHT (cm)"><Input value={vitals.height || ""} placeholder={ghost("height")} onChange={(e) => setVitals({ ...vitals, height: e.target.value })} className="tabular" data-testid="vitals-height" /></F>
              <F label="BMI"><Input value={bmi} readOnly className="tabular bg-slate-50" data-testid="vitals-bmi" /><div className="text-[10px] text-slate-500 mt-1">{bmi ? `${bmi} kg/m² · ${bmiCategory(parseFloat(bmi))}` : ""}</div></F>
              <F label="WAIST (cm)"><Input value={vitals.waist || ""} placeholder={ghost("waist")} onChange={(e) => setVitals({ ...vitals, waist: e.target.value })} className="tabular" data-testid="vitals-waist" /></F>
              <F label="SBP"><Input value={vitals.sbp || ""} placeholder={ghost("sbp")} onChange={(e) => setVitals({ ...vitals, sbp: e.target.value })} className="tabular" data-testid="vitals-sbp" /></F>
              <F label="DBP"><Input value={vitals.dbp || ""} placeholder={ghost("dbp")} onChange={(e) => setVitals({ ...vitals, dbp: e.target.value })} className="tabular" data-testid="vitals-dbp" /></F>
              <F label="PULSE"><Input value={vitals.pulse || ""} placeholder={ghost("pulse")} onChange={(e) => setVitals({ ...vitals, pulse: e.target.value })} className="tabular" data-testid="vitals-pulse" /></F>
              <F label="SpO₂ (%)"><Input value={vitals.spo2 || ""} placeholder={ghost("spo2")} onChange={(e) => setVitals({ ...vitals, spo2: e.target.value })} className="tabular" data-testid="vitals-spo2" /></F>
              <F label="TEMP (°C)"><Input value={vitals.temperature || ""} placeholder={ghost("temperature")} onChange={(e) => setVitals({ ...vitals, temperature: e.target.value })} className="tabular" data-testid="vitals-temp" /></F>
              <F label="RBS (mg/dL)"><Input value={vitals.random_sugar || ""} placeholder={ghost("random_sugar")} onChange={(e) => setVitals({ ...vitals, random_sugar: e.target.value })} className="tabular" data-testid="vitals-rbs" /></F>
              <F label="FBS"><Input value={vitals.fasting_sugar || ""} placeholder={ghost("fasting_sugar")} onChange={(e) => setVitals({ ...vitals, fasting_sugar: e.target.value })} className="tabular" data-testid="vitals-fbs" /></F>
              <F label="PPBS"><Input value={vitals.pp_sugar || ""} placeholder={ghost("pp_sugar")} onChange={(e) => setVitals({ ...vitals, pp_sugar: e.target.value })} className="tabular" data-testid="vitals-ppbs" /></F>
            </div>
            {vAlerts.length > 0 && (
              <div className="mt-3 space-y-1" data-testid="vitals-alerts">
                {vAlerts.map((a, i) => (
                  <div key={`${a.level}-${a.msg}-${i}`} className={`text-xs px-3 py-1.5 rounded flex items-center gap-2 ${a.level === "critical" ? "alert-critical" : "alert-warning"}`}>
                    <AlertTriangle size={12} /> {a.msg}
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section
            title="Chief Complaints & History"
            testid="section-history"
            filled={!!(chief || history.hpi || history.past_medical || history.drug_hx || history.family_hx)}
            summary={chief ? (chief.length > 60 ? `${chief.slice(0, 57)}…` : chief) : ""}
          >
            <F label="CHIEF COMPLAINTS">
              <div className="relative">
                <Textarea rows={2} value={chief} onChange={(e) => setChief(e.target.value)} data-testid="chief-complaints" className="pr-9" />
                <div className="absolute top-1 right-1">
                  <MicButton value={chief} onChange={setChief} testid="mic-chief" ariaLabel="Dictate chief complaints" />
                </div>
              </div>
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="HISTORY OF PRESENT ILLNESS">
                <div className="relative">
                  <Textarea rows={2} value={history.hpi} onChange={(e) => setHistory({ ...history, hpi: e.target.value })} className="pr-9" />
                  <div className="absolute top-1 right-1">
                    <MicButton value={history.hpi} onChange={(v) => setHistory({ ...history, hpi: v })} testid="mic-hpi" />
                  </div>
                </div>
              </F>
              <F label="PAST MEDICAL HISTORY">
                <div className="relative">
                  <Textarea rows={2} value={history.past_medical} onChange={(e) => setHistory({ ...history, past_medical: e.target.value })} data-testid="history-past-medical" className="pr-9" />
                  <div className="absolute top-1 right-1">
                    <MicButton value={history.past_medical} onChange={(v) => setHistory({ ...history, past_medical: v })} testid="mic-pmh" ariaLabel="Dictate past medical history" />
                  </div>
                </div>
              </F>
              <F label="DRUG HISTORY">
                <div className="relative">
                  <Textarea rows={2} value={history.drug_hx} onChange={(e) => setHistory({ ...history, drug_hx: e.target.value })} data-testid="history-drug-hx" className="pr-9" />
                  <div className="absolute top-1 right-1">
                    <MicButton value={history.drug_hx} onChange={(v) => setHistory({ ...history, drug_hx: v })} testid="mic-drug-hx" ariaLabel="Dictate drug history" />
                  </div>
                </div>
              </F>
              <F label="FAMILY HISTORY">
                <div className="relative">
                  <Textarea rows={2} value={history.family_hx} onChange={(e) => setHistory({ ...history, family_hx: e.target.value })} data-testid="history-family-hx" className="pr-9" />
                  <div className="absolute top-1 right-1">
                    <MicButton value={history.family_hx} onChange={(v) => setHistory({ ...history, family_hx: v })} testid="mic-family-hx" ariaLabel="Dictate family history" />
                  </div>
                </div>
              </F>
            </div>
            {(() => {
              const fh = familyHistoryInsights(history.family_hx);
              if (!fh.length) return null;
              const doneList = patient?.screenings_done || [];
              return (
                <div className="mt-2 space-y-1.5" data-testid="fh-insights">
                  <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Family-history insights · screening to consider</div>
                  {fh.map((r) => {
                    const st = screeningStatus(r.id, doneList);
                    if (st.satisfied) {
                      return (
                        <div key={r.id} className="text-[11px] leading-snug rounded-md border border-emerald-200 bg-emerald-50/60 px-2.5 py-1.5 flex items-center justify-between gap-2" data-testid={`fh-insight-done-${r.id}`}>
                          <span className="text-emerald-800">
                            ✓ <span className="font-semibold">{r.name}</span> — screening done {fmtScrDate(st.done_date)}
                            {st.due_date && <span className="text-emerald-600"> · due again {fmtScrMonth(st.due_date)}</span>}
                            <span className="text-emerald-600"> · hidden from Rx</span>
                          </span>
                          <button type="button" onClick={() => undoScreening(r.id)} className="text-[10px] text-emerald-700 underline shrink-0" data-testid={`fh-undo-${r.id}`}>Undo</button>
                        </div>
                      );
                    }
                    return (
                      <div key={r.id} className="text-[11px] leading-snug rounded-md border border-indigo-200 bg-indigo-50/50 px-2.5 py-1.5" data-testid={`fh-insight-${r.id}`}>
                        <span className="font-semibold text-indigo-900">{r.name}:</span>{" "}
                        <span className="text-slate-600">{r.note}</span>{" "}
                        <span className="text-slate-800 font-medium">→ {r.actions}</span>
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                          {st.overdue && (
                            <span className="text-[10px] text-amber-700 font-semibold" data-testid={`fh-overdue-${r.id}`}>
                              Last done {fmtScrDate(st.done_date)} — now due again
                            </span>
                          )}
                          <input
                            type="date"
                            value={screeningDates[r.id] || todayStr}
                            onChange={(e) => setScreeningDates({ ...screeningDates, [r.id]: e.target.value })}
                            className="h-6 text-[10px] border border-slate-300 rounded px-1 bg-white"
                            data-testid={`fh-done-date-${r.id}`}
                          />
                          <button type="button" onClick={() => markScreeningDone(r.id)} className="text-[10px] px-2.5 py-0.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700" data-testid={`fh-mark-done-${r.id}`}>
                            Mark done
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </Section>

          <Section
            title="Known Case Of"
            testid="section-known"
            filled={(known || []).length > 0}
            summary={(known || []).length ? known.slice(0, 3).join(", ") + (known.length > 3 ? ` +${known.length - 3}` : "") : ""}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] text-slate-500">
                Select all that apply. When "Sync to Diagnosis" is on, each ticked
                condition is added to <strong>Primary Diagnosis</strong> as a
                comma-separated list — untick to remove it there too.
              </p>
              <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-700 shrink-0">
                <Checkbox
                  checked={syncKnownToDx}
                  onCheckedChange={setSyncKnownToDx}
                  data-testid="sync-known-to-dx"
                />
                Sync to Diagnosis
              </label>
            </div>
            {KNOWN_DISEASE_GROUPS.map((g) => (
              <div key={g.label} className="mb-2" data-testid={`known-group-${g.label.replace(/\W+/g, "-").toLowerCase()}`}>
                <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{g.label}</div>
                <ChipMulti items={g.items} selected={known} onToggle={(v) => toggle(known, setKnown, v)} testid={`chip-known-${g.label.replace(/\W+/g, "-").toLowerCase()}`} />
              </div>
            ))}
            {known.includes("SJS/TEN (past drug reaction)") && (
              <div className="mt-2 p-2.5 rounded-md border border-red-300 bg-red-50/60" data-testid="sjs-culprit-box">
                <F label="SJS/TEN CULPRIT DRUG(S) — comma separated">
                  <Input
                    value={sjsCulprit}
                    onChange={(e) => setSjsCulprit(e.target.value)}
                    placeholder="e.g. Carbamazepine, Cotrimoxazole"
                    className="bg-white"
                    data-testid="sjs-culprit-input"
                  />
                </F>
                <div className="text-[10px] text-red-700 mt-1">
                  Any prescription containing this drug (or its cross-reactive class — aromatic antiepileptics, sulfonamides) will be hard-blocked at tap-time.
                </div>
              </div>
            )}
          </Section>

          <Section
            title="Known Complications"
            testid="section-complications"
            defaultOpen={(complications || []).length > 0}
            filled={(complications || []).length > 0}
            summary={(complications || []).length ? `${complications.length} recorded` : ""}
          >
            <ChipMulti items={COMPLICATIONS} selected={complications} onToggle={(v) => toggle(complications, setComplications, v)} testid="chip-comp" />
          </Section>

          <Section
            title="Allergies & Safety Flags"
            testid="section-allergies"
            defaultOpen={(allergyFlags || []).length > 0 || !!allergyText}
            filled={(allergyFlags || []).length > 0 || !!allergyText}
            summary={(allergyFlags || []).length ? `${allergyFlags.length} flag${allergyFlags.length === 1 ? "" : "s"}` : (allergyText ? "Text notes" : "")}
          >
            <ChipMulti items={ALLERGY_FLAGS} selected={allergyFlags} onToggle={(v) => toggle(allergyFlags, setAllergyFlags, v)} colorMode="warning" testid="chip-flag" />
            <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap gap-2" data-testid="patient-context-flags">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-full mb-1">Safety context (for DDI check)</div>
              {[
                { key: "alcoholMisuse", label: "Alcohol misuse", title: "Flag if patient drinks > 3 units/day or has known alcohol-use disorder — used by insulin/warfarin/metronidazole checks" },
                { key: "chronicSteroidUse", label: "Chronic steroid use", title: "Flag if on oral steroid > 5 mg prednisolone-eq for > 3 months — triggers bone-protection prompt" },
                { key: "isImmunosuppressed", label: "Immunosuppressed", title: "Flag if on cytotoxics, biologics, or high-dose steroid — blocks live vaccines" },
                { key: "nitrateUse", label: "Uses nitrates", title: "Flag if patient uses GTN/nitrates OUTSIDE this Rx (own supply / recent hospital) — blocks PDE5 inhibitors" },
                { key: "lactating", label: "Lactating / breastfeeding", title: "Flag if breastfeeding — screens every drug for infant safety and suggests lactation-safe alternatives" },
                { key: "g6pdDeficiency", label: "G6PD deficiency", title: "Flag known G6PD deficiency — blocks haemolysis-triggering drugs (sulfa, nitrofurantoin, dapsone, primaquine)" },
              ].map((f) => (
                <label key={f.key} className={`text-[11px] px-2.5 py-1 rounded-full border cursor-pointer flex items-center gap-1.5 ${patientFlags[f.key] ? "border-amber-400 bg-amber-50 text-amber-900" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`} title={f.title} data-testid={`flag-${f.key}-label`}>
                  <input
                    type="checkbox"
                    checked={!!patientFlags[f.key]}
                    onChange={(e) => setPatientFlags({ ...patientFlags, [f.key]: e.target.checked })}
                    className="w-3 h-3 accent-amber-600"
                    data-testid={`flag-${f.key}`}
                  />
                  {f.label}
                </label>
              ))}
            </div>
            <div className="mt-3">
              <F label="ALLERGY DETAILS (drug, food, contrast)"><Textarea rows={2} value={allergyText} onChange={(e) => setAllergyText(e.target.value)} /></F>
            </div>
          </Section>

          <Section
            title="Diagnosis"
            testid="section-diagnosis"
            filled={!!(diagnosis.primary || diagnosis.secondary)}
            summary={diagnosis.primary ? `${diagnosis.primary}${diagnosis.status && diagnosis.status !== "Under Evaluation" ? ` · ${diagnosis.status}` : ""}` : ""}
          >
            <PostmenopausalBleedingBanner
              patient={patient}
              chief={chief}
              known={known}
              diagnosis={diagnosis}
              acuteCondition={acuteCondition}
            />
            <div className="grid grid-cols-2 gap-3">
              <F label="PRIMARY DIAGNOSIS *"><Input value={diagnosis.primary} onChange={(e) => setDiagnosis({ ...diagnosis, primary: e.target.value })} data-testid="dx-primary" /></F>
              <F label="SECONDARY / ACUTE">
                <SecondaryDiagnosisCombobox
                  value={diagnosis.secondary}
                  onChange={(v) => setDiagnosis((d) => ({ ...d, secondary: v }))}
                  onKnownConditionChange={setAcuteCondition}
                />
              </F>
              <F label="ICD-10">
                <ICD10Combobox
                  value={diagnosis.icd10}
                  onChange={(v) => setDiagnosis((d) => ({ ...d, icd10: v }))}
                  onDiagnosisLabelPick={(label) => setDiagnosis((d) => (d.primary ? d : { ...d, primary: label }))}
                />
              </F>
              <F label="CLINICAL STATUS">
                <select value={diagnosis.status} onChange={(e) => setDiagnosis({ ...diagnosis, status: e.target.value })} className="h-9 w-full border border-slate-200 rounded px-2 text-sm" data-testid="dx-status">
                  {["Controlled", "Partially Controlled", "Uncontrolled", "Newly Diagnosed", "Under Evaluation", "Resolved"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </F>
            </div>
            <F label="NOTES"><Textarea rows={2} value={diagnosis.notes} onChange={(e) => setDiagnosis({ ...diagnosis, notes: e.target.value })} /></F>
            <AcuteSuggestionPanel
              condition={acuteCondition}
              currentMedicines={medicines}
              patientFlags={patientFlags}
              onAddMolecule={guardedAddMolecule}
              onAppendInvestigation={(lab) => {
                const trimmed = (nextInv || "").trim();
                const already = trimmed
                  .split(/[,\n]/)
                  .map((s) => s.trim().toLowerCase())
                  .includes(lab.trim().toLowerCase());
                if (already) return;
                setNextInv(trimmed ? `${trimmed}, ${lab}` : lab);
              }}
              onAppendAdvice={(line) => {
                setAdvice((prev) => {
                  const trimmed = (prev || "").trimEnd();
                  return trimmed ? `${trimmed}\n${line}` : line;
                });
                // Chips always append to the detailed source. If the doctor was
                // viewing a summary, mark it stale so they know to re-compress.
                setAdviceView("detailed");
                if (adviceSummary) setAdviceSummary("");
                toast.success("Reasoning copied to Advice / Rx");
              }}
            />
          </Section>


          <Section
            title="Reports"
            testid="section-reports"
            defaultOpen={false}
            filled={reports.length > 0}
            summary={reports.length ? `${reports.length} on file` : ""}
          >
            <p className="text-[11px] text-slate-500 mb-2">
              Upload the raw report file (PDF or image) for the record. It becomes a permanent, viewable attachment on the patient's timeline — you or the patient can open it later, and the AI extract button on each card can also pull lab values into the Investigations table above.
            </p>
            <label
              className="inline-flex items-center gap-2 px-4 py-2 border border-dashed border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 text-sm"
              title="Attaches the full report file to this patient. Separate from 'AI extract lab values' — that one pulls numbers into the Investigations table without saving the file."
              data-testid="upload-report-label"
            >
              <FileUp size={14} /> Upload full report file (PDF or image)
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={uploadReport} className="hidden" data-testid="upload-report-input" />
            </label>
            <button
              type="button"
              onClick={() => setPasteOpen(!pasteOpen)}
              className="ml-2 inline-flex items-center gap-2 px-4 py-2 border border-dashed border-slate-300 rounded-md hover:bg-slate-50 text-sm"
              title="Copy-paste any report text — USG, CT, MRI, X-ray, PET, echo, angiography, bone marrow, biopsy or lab. AI classifies it and files it on the record."
              data-testid="paste-report-btn"
            >
              <FileText size={14} /> Paste report text
            </button>
            {pasteOpen && (
              <div className="mt-3 border border-slate-200 rounded-md p-3 bg-slate-50/60" data-testid="paste-report-panel">
                <p className="text-[11px] text-slate-500 mb-2">
                  Paste the full text of any report — USG, CT, MRI, X-ray, PET, 2D echo, angiography, endoscopy, bone marrow, biopsy or lab. AI extracts the impression / lab values, files it on the patient's record, and feeds the findings into prescribing safety + AI guidance.
                </p>
                <Textarea rows={6} value={pasteText} onChange={(e) => setPasteText(e.target.value)} placeholder="Paste the report text here…" data-testid="paste-report-text" />
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={submitPaste} disabled={pasteBusy} data-testid="paste-report-save">{pasteBusy ? "Extracting…" : "Extract & save"}</Button>
                  <Button size="sm" variant="outline" onClick={() => { setPasteOpen(false); setPasteText(""); }} data-testid="paste-report-cancel">Cancel</Button>
                </div>
              </div>
            )}
            {imagingReports.length > 0 && (
              <div className="mt-3 border border-sky-200 bg-sky-50/50 rounded-md p-3" data-testid="imaging-context-panel">
                <div className="text-[10px] uppercase tracking-wider text-sky-800 font-bold">Imaging & procedures on file · feeding safety + AI guidance</div>
                <div className="mt-1.5 space-y-1.5">
                  {imagingReports.slice(0, 6).map((r) => (
                    <div key={r.report_id} className="text-[11px] leading-snug" data-testid={`imaging-report-${r.report_id}`}>
                      <span className="font-semibold text-sky-900">{r.imaging.study || r.imaging.modality}</span>
                      <span className="text-slate-500"> · {r.report_date || new Date(r.uploaded_at).toLocaleDateString("en-IN")}</span>
                      {r.imaging.impression && <span className="text-slate-700"> — {r.imaging.impression}</span>}
                    </div>
                  ))}
                </div>
                {imagingFindings.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {imagingFindings.map((f, i) => (
                      <span
                        key={`${f.source}-${f.text}`}
                        className="inline-flex items-center gap-1 text-[10px] bg-white border border-sky-300 text-sky-900 rounded-full px-2 py-0.5"
                        title={`From ${f.source}${f.date ? ` (${f.date})` : ""} — feeds the safety engine; tap × to exclude this visit`}
                        data-testid={`imaging-chip-${i}`}
                      >
                        {f.text}
                        <button type="button" onClick={() => setRemovedFindings((prev) => [...prev, f.text.toLowerCase()])} className="text-sky-500 hover:text-rose-600" data-testid={`imaging-chip-remove-${i}`}>
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
              {reports.map((r) => {
                const isManual = r.entry_mode === "manual" || r.extraction_status === "manual";
                return (
                <div key={r.report_id} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`report-card-${r.report_id}`}>
                  <div className="text-xs font-medium truncate">{r.file_name}</div>
                  <div className="text-[10px] text-slate-500 mt-1">{r.category} · {r.report_date || new Date(r.uploaded_at).toLocaleDateString()}</div>
                  {r.extraction_status && (
                    <div className={`text-[10px] mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                      isManual ? "bg-sky-50 text-sky-700"
                      : r.extraction_status === "ok" ? "bg-emerald-50 text-emerald-700"
                      : r.extraction_status === "pending" ? "bg-amber-50 text-amber-700"
                      : r.extraction_status === "failed" ? "bg-rose-50 text-rose-700"
                      : "bg-slate-50 text-slate-500"
                    }`} data-testid={`report-status-${r.report_id}`}>
                      {isManual && `Manual · ${(r.extracted_tests || []).length} labs · by ${(r.uploaded_by || "").split("@")[0] || "staff"}`}
                      {!isManual && r.extraction_status === "ok" && `AI · ${(r.extracted_tests || []).length} labs`}
                      {!isManual && r.extraction_status === "pending" && "AI extracting…"}
                      {!isManual && r.extraction_status === "failed" && "AI failed"}
                      {!isManual && r.extraction_status === "skipped" && "no auto-extract"}
                    </div>
                  )}
                  <div className="mt-2 flex gap-2">
                    {r.url ? (
                      <a href={`${API}${r.url.replace("/api", "")}`} target="_blank" rel="noreferrer" className="text-xs text-pink-600 hover:underline">Open</a>
                    ) : null}
                    <button onClick={() => extractAI(r.report_id)} className="text-xs text-pink-600 hover:underline flex items-center gap-1" data-testid={`extract-ai-${r.report_id}`}>
                      <Sparkles size={11} />
                      {isManual ? "Merge" : (r.extraction_status === "ok" ? "Merge" : "Extract")}
                    </button>
                  </div>
                </div>
                );
              })}
              {reports.length === 0 && <div className="text-xs text-slate-500 col-span-full">No reports uploaded yet.</div>}
            </div>
          </Section>

          {prevVisits.length > 0 && (
            <Section title="Trends" testid="section-trends" defaultOpen={false}>
              <TrendCharts patientId={pid} />
            </Section>
          )}

          <Section
            title="WhatsApp Messages"
            testid="section-wa-history"
            defaultOpen={false}
            filled={waMessages.length > 0}
            summary={waMessages.length ? `${waMessages.length} sent` : ""}
            icon={<MessageCircle size={14} />}
          >
            <p className="text-[11px] text-slate-500 mb-2">
              Everything sent to this patient over WhatsApp — prescriptions, care-kit links, reminders and shares — so staff can see what was already sent before sending again.
            </p>
            {waMessages.length === 0 && <div className="text-xs text-slate-500" data-testid="wa-history-empty">No WhatsApp messages logged for this patient yet.</div>}
            <div className="space-y-1.5">
              {waMessages.map((m, i) => (
                <div key={m.message_id || i} className="border border-slate-200 rounded-md px-2.5 py-1.5 bg-white text-[11px]" data-testid={`wa-msg-row-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-700 tabular">{m.created_at ? new Date(m.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                    <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{(m.kind || "general").replace(/_/g, " ")}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{m.channel === "manual" ? "sent from clinic phone" : m.channel}</span>
                    {m.by && <span className="text-[10px] text-slate-400">by {(m.by || "").split("@")[0]}</span>}
                  </div>
                  {m.preview && <div className="mt-1 text-slate-600 whitespace-pre-line line-clamp-2">{m.preview}</div>}
                </div>
              ))}
            </div>
          </Section>

          {/* Multi-series lab comparison graph — FBS / PPBS / HbA1c / Creatinine
              / LDL / TG (plus HDL, TC, eGFR, TSH as toggle chips) with a
              6m / 12m / All window switcher. Complements the per-metric mini
              charts above by putting every core lab on one time axis. */}
          <Section title="Lab Comparison Graph" testid="section-lab-comparison" defaultOpen={false} icon={<FlaskConical size={14} />}>
            <LabTrendsChart patientId={pid} />
          </Section>


          <Section title="Next Steps" testid="section-next-steps" defaultOpen={false}>
            <NextStepsPanel
              patientId={pid}
              preferredLanguage={patient.preferred_language}
              snapshot={{
                chief_complaints: chief,
                diagnosis: diagnosis?.primary,
                known_case_of: known,
                allergy_flags: allergyFlags,
        patient_flags: patientFlags,
                medicines,
                investigations,
                vitals,
              }}
              onSetFollowUp={({ follow_up, follow_up_date }) => {
                if (follow_up) setFollowUpDays(follow_up);
              }}
              onAddInvestigation={(lab) => {
                const trimmed = (nextInv || "").trim();
                const already = trimmed.split(/[,\n]/).map((s) => s.trim().toLowerCase()).includes(lab.trim().toLowerCase());
                if (already) return;
                setNextInv(trimmed ? `${trimmed}, ${lab}` : lab);
              }}
              onAppendAdvice={(line) => {
                setAdvice((prev) => (prev ? `${prev}\n${line}` : line));
              }}
              onAcceptForPrint={(structured) => setNextSteps(structured)}
            />
          </Section>


          {prevVisits.length > 0 && (
            <Section title="Upcoming Follow-ups" testid="section-upcoming">
              <UpcomingCalendar testid="patient-calendar" patientId={pid} days={180} />
            </Section>
          )}

          {prevVisits.length > 0 && (
            <Section title="Previous Visits" testid="section-prev" defaultOpen={false} summary={`${prevVisits.length} recorded`}>
              <div className="space-y-2">
                {prevVisits.slice(0, 5).map((v) => (
                  <div key={v.visit_id} className="border border-slate-200 rounded-md p-3 text-sm" data-testid={`prev-visit-${v.visit_id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{v.diagnosis?.primary || "—"}</div>
                        <div className="text-xs text-slate-500 mt-1">BP {v.vitals?.sbp}/{v.vitals?.dbp} · BMI {v.vitals?.bmi} · RBS {v.vitals?.random_sugar}</div>
                        {(v.medicines || []).length > 0 && (
                          <div className="text-[11px] text-slate-600 mt-1 flex items-start gap-1">
                            <Pill size={11} className="mt-0.5 text-slate-400 shrink-0" />
                            <span className="truncate">
                              {v.medicines.slice(0, 3).map((m) => m.brand_name || m.generic_name).filter(Boolean).join(", ")}
                              {v.medicines.length > 3 && <span className="text-slate-400"> +{v.medicines.length - 3}</span>}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <div className="text-xs text-slate-500 tabular">{new Date(v.visit_date).toLocaleDateString()}</div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => cloneFromVisit(v)}
                            className="px-2 py-1 text-[11px] rounded border border-pink-300 text-pink-700 hover:bg-pink-50 flex items-center gap-1"
                            data-testid={`clone-visit-${v.visit_id}`}
                            title="Clone this visit's prescription into the current form"
                          >
                            <Copy size={11} /> Clone
                          </button>
                          <button
                            onClick={() => { window.location.href = `/patient/${pid}?edit=${v.visit_id}`; }}
                            className="px-2 py-1 text-[11px] rounded border border-amber-300 text-amber-700 hover:bg-amber-50 flex items-center gap-1"
                            data-testid={`edit-visit-${v.visit_id}`}
                            title="Edit this visit & its prescription in place (no duplicate)"
                          >
                            <Pencil size={11} /> Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* RIGHT column – sticky prescription */}
        <div className={`min-w-0 ${layout === "full" ? "" : layout === "wide" ? "md:col-span-7" : "md:col-span-4"}`}>
          <div className={layout === "full" ? "space-y-4" : "sticky top-[72px] space-y-4"}>
            <div id="prescription-panel" className="bg-white border border-slate-200 rounded-md scroll-mt-24">
              <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
                <div className="section-title">Prescription</div>
                <div className="flex flex-wrap items-center gap-2 visit-toolbar">
                  <GuidancePanel
                    patientId={pid}
                    patient={patient}
                    vitals={vitals}
                    bmi={bmi}
                    labs={(() => {
                      const map = {};
                      investigations.forEach((inv) => {
                        const key = canonicalKey(inv.test_name);
                        if (key && inv.result && map[key] === undefined) map[key] = inv.result;
                      });
                      return map;
                    })()}
                    allergyFlags={allergyFlags}
                    comorbidities={known}
                    diagnosisPrimary={diagnosis.primary}
                    diagnosisSecondary={diagnosis.secondary}
                    chief={chief}
                    historyText={[
                      history.past_medical && `Past medical: ${history.past_medical}`,
                      history.drug_hx && `Ongoing/outside drugs (drug history): ${history.drug_hx}`,
                      history.family_hx && `Family history: ${history.family_hx}`,
                      history.personal_hx && `Personal: ${history.personal_hx}`,
                    ].filter(Boolean).join("\n")}
                    medicines={medicines}
                    onAddMolecule={guardedAddMolecule}
                    onAddNextInvestigation={(lab) => setNextInv((prev) => {
                      const trimmed = (prev || "").trim();
                      const already = trimmed.split(/[,\n]/).map((s) => s.trim().toLowerCase()).includes((lab || "").trim().toLowerCase());
                      if (already) return prev;
                      return trimmed ? `${trimmed}, ${lab}` : lab;
                    })}
                    onAppendAdvice={(line) => setAdvice((prev) => (prev ? `${prev}\n${line}` : line))}
                  />
                  <TemplatePicker
                    onApply={(t) => {
                      // PROV: physician-selected static template — origin PHYSICIAN/template (ruling D3)
                      const meds = ((t.medicines || []).length ? t.medicines : [emptyRow()]).map((m) => (m.brand_name ? { ...m, _prov: { origin: "PHYSICIAN", detail: "template" } } : m));
                      setMedicines(meds);
                      meds.filter((m) => m.brand_name).forEach((m) => provLog({ event_type: "MED_ADDED", session_id: provSessionRef.current, patient_internal_id: pid, medicine: medEventFields(m), medicine_origin: "PHYSICIAN", origin_detail: "template" })); // PROV
                      if (t.diagnosis) setDiagnosis((d) => ({ ...d, primary: t.diagnosis, icd10: t.icd10 || d.icd10 }));
                      if (t.advice) setAdvice(t.advice);
                      if (t.follow_up) setFollowUpDays(t.follow_up);
                      if (t.next_investigations) setNextInv(t.next_investigations);
                      toast.success("Template applied — please review before saving");
                    }}
                    currentMedicines={medicines}
                    currentDiagnosis={diagnosis}
                    currentAdvice={advice}
                    currentFollowUp={followUpDays}
                    currentNextInv={nextInv}
                  />
                  <Button size="sm" onClick={addRow} className="bg-slate-900 hover:bg-slate-800 text-white h-8 gap-1" data-testid="add-medicine-row"><Plus size={12} /> Row</Button>
                  <Button size="sm" onClick={addInsulinRow} className="bg-amber-500 hover:bg-amber-600 text-white h-8 gap-1" data-testid="add-insulin-row"><Syringe size={12} /> Insulin</Button>
                </div>
              </div>
              {prevVisits[0] && (
                <div className="px-4 py-2.5 border-b border-pink-100 bg-pink-50/60 flex items-center justify-between gap-3" data-testid="ctall-banner">
                  <div className="text-[11px] text-pink-900 min-w-0">
                    <strong>Ct&nbsp;all</strong> — Continue same medicines from{" "}
                    <span className="font-medium">
                      {new Date(prevVisits[0].visit_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                    {" "}({(prevVisits[0].medicines || []).length} med{(prevVisits[0].medicines || []).length === 1 ? "" : "s"})
                    <span className="text-pink-700/70"> · brings dose, freq, duration, advice & follow-up into this visit — review before saving.</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => cloneFromVisit(prevVisits[0])}
                    className="h-7 gap-1 bg-pink-600 hover:bg-pink-700 text-white shrink-0"
                    data-testid="ctall-continue-btn"
                    title="Continue same medicines from the last visit (Ct all)"
                  >
                    <Copy size={11} /> Ct all
                  </Button>
                </div>
              )}
              {/* Brand-tier + FDC toggle bar — non-destructive, reversible */}
              {(() => {
                const eligibleRows = medicines.filter((m) => m?.brand_name);
                if (!eligibleRows.length) return null;
                const fdcs = detectFDCs(medicines);
                const anySwapped = medicines.some((m) => m?._origBrand || m?._fdcCombined);
                const activeTier = (typeof window !== "undefined" && localStorage.getItem("brand_tier_mode")) || "original";
                const preferredCompanies = (CLINIC.preferred_companies && CLINIC.preferred_companies.length)
                  ? CLINIC.preferred_companies
                  : ["Eris","Lupin","Mankind","Glenmark","Dr Reddy's","Cipla","GDP Life Sciences","Renwik","Macleods"];
                const setTier = (tier) => {
                  if (typeof window !== "undefined") localStorage.setItem("brand_tier_mode", tier);
                  if (tier === "original") { setMedicines(restoreOriginal(medicines)); toast.success("Restored original brands"); return; }
                  if (tier === "mychoice") {
                    // Restore first so we don't stack swaps on already-swapped brands.
                    const base = restoreOriginal(medicines);
                    const next = switchToMyChoice(base, preferredCompanies, pickMyChoiceBrand);
                    setMedicines(next);
                    toast.success("Switched to My Choice brands");
                    // Then refine against the doctor's own list — real stock + prices.
                    refineMyChoiceFromList(next);
                    return;
                  }
                  const base = restoreOriginal(medicines);
                  setMedicines(switchBrandTier(base, tier));
                  toast.success(tier === "economical" ? "Switched to Economical brands" : "Switched to Premium brands");
                };
                const combineAll = () => {
                  const next = applyAllFDCs(medicines);
                  const merged = medicines.length - next.length;
                  if (!merged) { toast.info("No compatible FDC combinations found"); return; }
                  setMedicines(next);
                  toast.success(`Merged ${merged + 1} rows into ${fdcs.length} fixed-dose combination${fdcs.length === 1 ? "" : "s"}`);
                };
                const combineOne = (fdcMatch) => {
                  setMedicines(applyFDC(medicines, fdcMatch));
                  toast.success(`Combined into ${fdcMatch.preview.brand_name}`);
                };
                return (
                  <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70" data-testid="brand-tier-bar">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Brands</span>
                      <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid="brand-tier-toggle">
                        <button
                          onClick={() => setTier("original")}
                          className={`px-2.5 py-1 text-[11px] font-medium ${!anySwapped ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}
                          data-testid="brand-tier-original"
                        >Original</button>
                        <button
                          onClick={() => setTier("economical")}
                          className={`px-2.5 py-1 text-[11px] font-medium border-l border-slate-300 ${activeTier === "economical" && anySwapped ? "bg-emerald-600 text-white" : "bg-white text-emerald-700 hover:bg-emerald-50"}`}
                          data-testid="brand-tier-economical"
                          title="Swap every row's brand to the cheapest reliable Indian generic"
                        >Economical</button>
                        <button
                          onClick={() => setTier("premium")}
                          className={`px-2.5 py-1 text-[11px] font-medium border-l border-slate-300 ${activeTier === "premium" && anySwapped ? "bg-indigo-600 text-white" : "bg-white text-indigo-700 hover:bg-indigo-50"}`}
                          data-testid="brand-tier-premium"
                          title="Swap every row's brand to the innovator / patent-holder"
                        >Premium</button>
                        <button
                          onClick={() => setTier("mychoice")}
                          className={`px-2.5 py-1 text-[11px] font-medium border-l border-slate-300 ${activeTier === "mychoice" && anySwapped ? "bg-pink-600 text-white" : "bg-white text-pink-700 hover:bg-pink-50"}`}
                          data-testid="brand-tier-mychoice"
                          title={`Swap every row's brand to your preferred pharma companies (priority: ${preferredCompanies.slice(0,3).join(" → ")}…)`}
                        >My Choice</button>
                        <button
                          onClick={cheapestAllRx}
                          className="px-2.5 py-1 text-[11px] font-semibold border-l border-slate-300 bg-white text-emerald-700 hover:bg-emerald-50"
                          data-testid="brand-tier-cheapest"
                          title="Swap every row to the cheapest equivalent from YOUR drug list and show the total ₹ saved"
                        >₹ Cheapest Rx</button>
                      </div>
                      {fdcs.length > 0 && (
                        <Button
                          size="sm"
                          onClick={combineAll}
                          className="h-7 gap-1 bg-pink-600 hover:bg-pink-700 text-white text-[11px]"
                          data-testid="fdc-combine-all"
                          title="Merge every detected pair/triple into fixed-dose combinations"
                        >
                          <Pill size={11} /> Combine all ({fdcs.length})
                        </Button>
                      )}
                      <InteractionCheckButton
                        medicines={medicines}
                        patient={patient}
                        known={known}
                        flags={patientFlags}
                        onDeprescribe={(idx, note) => {
                          const removed = medicines[idx];
                          if (removed?.brand_name) { const o = originOfRow(removed); provLog({ event_type: "MED_REMOVED", session_id: provSessionRef.current, patient_internal_id: pid, medicine: medEventFields(removed), medicine_origin: o.origin, origin_detail: o.detail, physician_action: "MEDICINE_REMOVED", evidence_text: note }); } // PROV
                          setMedicines((prev) => prev.filter((_, i) => i !== idx));
                          setAdvice((prev) => (prev ? `${prev}\n${note}` : note));
                          toast.success(`Removed ${removed?.brand_name || removed?.generic_name} — audit note added`);
                        }}
                        egfr={(() => { const scr = parseFloat(investigations.find((i) => /creat/i.test(i.test_name || ""))?.result); const a = parseFloat(patient?.age); return (scr && a) ? Math.round(ckdEpi2021(scr, a, patient?.sex)) : null; })()}
                        labs={(() => { const map = {}; investigations.forEach((i) => { const k = canonicalKey(i.test_name); if (k && i.result) map[k] = parseFloat(i.result); }); return map; })()}
                      />
                      {anySwapped && (
                        <span className="text-[10px] text-slate-500 italic ml-auto">Reversible — tap Original to restore</span>
                      )}
                    </div>
                    {fdcs.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="fdc-chip-list">
                        {fdcs.map((f) => (
                          <button
                            key={f.key}
                            onClick={() => combineOne(f)}
                            className="text-[11px] px-2 py-1 rounded-full border border-pink-200 bg-white text-pink-800 hover:bg-pink-50 flex items-center gap-1"
                            data-testid={`fdc-combine-${f.key}`}
                            title={`Merges rows ${f.rowIdxs.map((i) => i + 1).join(" + ")} into ${f.preview.brand_name}`}
                          >
                            <Pill size={10} /> Combine → <strong className="font-semibold">{f.preview.brand_name}</strong>
                            <span className="text-slate-400">({f.preview.strength})</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className={`overflow-x-auto ${layout === "full" ? "max-h-none" : "max-h-[640px]"} overflow-y-auto`}>
                <table className="w-full text-sm visit-responsive-table visit-medicine-table">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="p-2 w-8 sticky left-0 top-0 z-30 bg-slate-50">#</th><th className="p-2 min-w-[260px] sticky left-8 top-0 z-30 bg-slate-50 border-r border-slate-100">Medicine</th><th className="p-2">Dose</th><th className="p-2">Route</th><th className="p-2">Freq</th><th className="p-2">Timing</th><th className="p-2">Duration</th><th className="p-2">Qty</th><th className="p-2">Instructions</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {medicines.map((m, i) => (
                      m.type === "insulin"
                        ? <InsulinRow key={m._uid || `med-${i}`} row={m} index={i} onChange={(r) => updRow(i, r)} onDuplicate={() => dupRow(i)} onDelete={() => delRow(i)} />
                        : <MedicineRow key={m._uid || `med-${i}`} row={m} index={i} egfr={latestLabs?.egfr?.value} onChange={(r) => updRow(i, r)} onDuplicate={() => dupRow(i)} onDelete={() => delRow(i)} onPickGuard={(r) => guardRowPick(i, r)} />
                    ))}
                  </tbody>
                </table>
              </div>
              {liveCost && liveCost.count > 0 && (
                liveCost.total > 0 ? (
                  <div className="px-3 py-1.5 border-t border-slate-200 bg-emerald-50/50 flex items-baseline justify-end gap-1.5" data-testid="live-cost-strip">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Estimated medicine cost</span>
                    <span className="text-[13px] font-bold text-emerald-800 tabular">₹{liveCost.total.toFixed(0)}</span>
                    <span className="text-[10px] text-slate-400">({liveCost.priced}/{liveCost.count} priced · clinic list · whole packs)</span>
                  </div>
                ) : (
                  <div className="px-3 py-1.5 border-t border-slate-200 bg-amber-50/50 flex items-baseline justify-end gap-1.5" data-testid="live-cost-strip-nomatch">
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Estimated medicine cost</span>
                    <span className="text-[10px] text-slate-500">no price match in My Drug List for {liveCost.count === 1 ? "this medicine" : "these medicines"} — pick a brand from the list (or use My Choice) to see ₹</span>
                  </div>
                )
              )}
              <GuidelineDoseStrip
                medicines={medicines}
                known={known}
                complications={complications}
                onApplyDose={(i, dose) => updRow(i, { ...medicines[i], dose })}
              />
              {/* #3: Already-on titration strip — one-tap up/down the ladder */}
              {(() => {
                const rows = medicines.map((m, i) => ({ m, i, opts: titrationOptions(m) })).filter((r) => r.opts && (r.opts.up || r.opts.down));
                if (!rows.length) return null;
                return (
                  <div className="px-3 py-2 border-t border-slate-200 flex flex-wrap items-center gap-2" data-testid="titration-strip">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Titrate</span>
                    {rows.map(({ m, i, opts }) => (
                      <span key={m._uid || i} className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 border border-emerald-200 rounded-full pl-2 pr-1 py-0.5 text-emerald-900">
                        ✓ {m.brand_name || m.generic_name}
                        {opts.up && (
                          <button
                            onClick={() => { updRow(i, retitrateRow(m, opts.up)); toast.success(`${m.generic_name || m.brand_name}: up-titrated to ${opts.up} mg`); }}
                            className="px-1.5 py-0.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 text-[10px] font-semibold"
                            title={`Increase to ${opts.up} mg`}
                            data-testid={`titrate-up-${i}`}
                          >↑ {opts.up}</button>
                        )}
                        {opts.down && (
                          <button
                            onClick={() => { updRow(i, retitrateRow(m, opts.down)); toast.success(`${m.generic_name || m.brand_name}: down-titrated to ${opts.down} mg`); }}
                            className="px-1.5 py-0.5 rounded-full bg-amber-500 text-white hover:bg-amber-600 text-[10px] font-semibold"
                            title={`Reduce to ${opts.down} mg`}
                            data-testid={`titrate-down-${i}`}
                          >↓ {opts.down}</button>
                        )}
                      </span>
                    ))}
                  </div>
                );
              })()}
              {dAlerts.length > 0 && (
                <div className="p-3 border-t border-slate-200 space-y-1" data-testid="drug-alerts">
                  {dAlerts.map((a, i) => (
                    <div key={`${a.level}-${a.msg}-${i}`} className={`text-xs px-2 py-1.5 rounded flex items-center gap-2 ${
                      a.level === "critical" ? "alert-critical" : (a.level === "major" || a.level === "caution") ? "alert-warning" : "alert-info"
                    }`}>
                      <AlertTriangle size={12} /> {a.msg}
                    </div>
                  ))}
                  <label className="flex items-center gap-2 text-xs mt-2">
                    <Checkbox checked={overrideAlerts} onCheckedChange={setOverrideAlerts} data-testid="override-alerts" />
                    Override alerts (reason required)
                  </label>
                  {overrideAlerts && (
                    <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Clinical justification" className="text-xs mt-1" data-testid="override-reason" />
                  )}
                </div>
              )}
            </div>
            {/* -- Post-Prescription Panel: Advice → Vaccination → Education → Save.
                 Moved out of the left column so the doctor's eye flows from the
                 Rx table straight into the follow-up, immunisation and handouts
                 blocks before hitting the final Save & Preview Rx button. -- */}
          <Section
            title="Advice & Follow-up"
            testid="section-advice"
            filled={!!(advice || adviceSummary)}
            summary={followUpDays ? `Follow-up in ${followUpDays}` : ""}
            right={
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <PatientLanguageSelect value={adviceLang} onChange={setAdviceLang} testId="advice-generate-lang" label="Advice language" />
                <button
                  type="button"
                  onClick={generateAdviceFollowup}
                  disabled={generatingAdvice}
                  className="px-3 py-1.5 rounded-md border border-pink-300 bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50 inline-flex items-center gap-1.5 shadow-sm"
                  data-testid="advice-generate-btn"
                  title="AI-generate the whole Advice + Follow-up + Next-labs from this patient's snapshot"
                >
                  <Sparkles size={13} /> {generatingAdvice ? "Generating…" : "Generate Advice & Follow-up"}
                </button>
              </div>
            }
          >
            <F label="ADVICE">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
                    <button
                      type="button"
                      onClick={() => setAdviceView("detailed")}
                      className={`px-3 py-1 ${adviceView === "detailed" ? "bg-pink-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                      data-testid="advice-tab-detailed"
                    >
                      Detailed
                    </button>
                    <button
                      type="button"
                      onClick={() => adviceSummary && setAdviceView("summary")}
                      disabled={!adviceSummary}
                      className={`px-3 py-1 border-l border-slate-200 ${adviceView === "summary" ? "bg-pink-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-white"}`}
                      data-testid="advice-tab-summary"
                      title={adviceSummary ? "Show compressed 3-line summary" : "Run Compress & Cite first"}
                    >
                      Summary{adviceSummary ? "" : " (empty)"}
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 visit-toolbar">
                    <select
                      value={adviceStyle}
                      onChange={(e) => setAdviceStyle(e.target.value)}
                      className="text-[11px] border border-slate-200 rounded px-2 py-1 bg-white"
                      data-testid="advice-style-select"
                      title="Choose summary style before pressing Compress & Cite"
                    >
                      <option value="prose">3-line prose</option>
                      <option value="bullets">3-5 bullets</option>
                    </select>
                    <button
                      type="button"
                      onClick={compressAdvice}
                      disabled={compressing || !(advice || "").trim()}
                      className="text-[11px] px-2.5 py-1 rounded-md border border-pink-200 bg-pink-50 text-pink-700 hover:bg-pink-100 disabled:opacity-50 inline-flex items-center gap-1"
                      data-testid="advice-compress-btn"
                      title="AI-compress the detailed reasoning into a clean printable summary"
                    >
                      <Sparkles size={12} /> {compressing ? "Compressing…" : "Compress & Cite"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAdviceView("detailed"); toast.info("Restored detailed advice"); }}
                      disabled={adviceView === "detailed"}
                      className="text-[11px] px-2.5 py-1 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1"
                      data-testid="advice-restore-btn"
                      title="Discard the summary view and go back to detailed advice"
                    >
                      <ArrowLeft size={12} /> Restore
                    </button>
                  </div>
                </div>
                <div className="relative">
                  {adviceView === "summary" ? (
                    <Textarea
                      rows={4}
                      value={adviceSummary}
                      onChange={(e) => setAdviceSummary(e.target.value)}
                      placeholder="Compressed summary appears here after Compress & Cite…"
                      data-testid="advice-summary-input"
                      className="pr-9 bg-pink-50/40"
                    />
                  ) : (
                    <Textarea
                      rows={4}
                      value={advice}
                      onChange={(e) => { setAdvice(e.target.value); if (adviceSummary) setAdviceSummary(""); }}
                      placeholder="Diet, exercise, monitoring… (chips from calculators/AI push append here)"
                      data-testid="advice-input"
                      className="pr-9"
                    />
                  )}
                  <div className="absolute top-1 right-1">
                    {adviceView === "summary"
                      ? <MicButton value={adviceSummary} onChange={setAdviceSummary} testid="mic-advice-summary" ariaLabel="Dictate summary" />
                      : <MicButton value={advice} onChange={setAdvice} testid="mic-advice" ariaLabel="Dictate advice" />
                    }
                  </div>
                </div>
                {adviceView === "summary" && adviceSummary && (
                  <div className="text-[10px] text-slate-500">
                    Prints the compressed summary. Full detailed reasoning is preserved internally for the audit trail.
                  </div>
                )}
              </div>
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="FOLLOW-UP AFTER">
                <div className="flex gap-2 flex-wrap items-center">
                  {FOLLOWUP_PRESETS.map((p) => (
                    <button key={p} onClick={() => setFollowUpDays(p)} className={`px-3 py-1 rounded-full text-xs border ${followUpDays === p ? "bg-pink-600 text-white border-pink-600" : "bg-white border-slate-200 hover:bg-slate-50"}`} data-testid={`fup-${p.replace(" ", "-")}`}>{p}</button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById("follow-up-days-input");
                      if (el) { el.focus(); el.select?.(); }
                    }}
                    className="px-3 py-1 rounded-full text-xs border border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1"
                    data-testid="fup-manual-btn"
                    title="Type a custom follow-up interval — e.g. 21 days, 10 weeks"
                  >
                    ✎ Follow-up (manual)
                  </button>
                </div>
                <Input id="follow-up-days-input" value={followUpDays} onChange={(e) => setFollowUpDays(e.target.value)} className="mt-2" data-testid="follow-up-days" placeholder="e.g. 21 days, 10 weeks, 3 months" />
              </F>
              <F label="NEXT INVESTIGATIONS"><Textarea rows={3} value={nextInv} onChange={(e) => setNextInv(e.target.value)} placeholder="e.g. HbA1c, FBS/PPBS, RFT" data-testid="next-investigations" /></F>
            </div>

            {/* -------- Vaccines to schedule -------- */}
            <VaccinesPlanner
              value={vaccinesPlanned}
              onChange={setVaccinesPlanned}
              catalog={vaccineCatalog}
              patientId={pid}
              autoSuggest={searchParams.get("suggestVaccines") === "1"}
              onAutoSuggested={() => { searchParams.delete("suggestVaccines"); setSearchParams(searchParams, { replace: true }); }}
            />
          </Section>

          <Section title="Vaccinations" testid="section-vaccinations" defaultOpen={false}>
            <Vaccinations patientId={pid} hasDob={!!patient?.dob} />
          </Section>

          {(() => {
            const kitCount = EDU_KITS.filter((k) => k.detect(medicines, { knownText: guardCtx.knownText, egfr: bestEgfr }).matched).length;
            return (
          <Section
            title="Patient Education Handouts"
            testid="section-education"
            defaultOpen={false}
            filled={(educationIds || []).length > 0 || kitCount > 0}
            badge={kitCount > 0 ? `${kitCount} care kit${kitCount > 1 ? "s" : ""} detected` : null}
            autoOpenWhen={kitCount > 0}
            summary={(educationIds || []).length ? `${educationIds.length} selected for print` : ""}
          >
            <GLP1CompanionKitPanel patientId={pid} medicines={medicines} defaultLang={patient.preferred_language || "mr"} lockedLanguage={!!patient.preferred_language} />
            <EduKitPanel patientId={pid} patient={patient} medicines={medicines} ctx={{ knownText: guardCtx.knownText, egfr: bestEgfr }} defaultLang={patient.preferred_language || "mr"} />
            <PatientEducationHandouts patient={patient} known={known} medicines={medicines} allergyFlags={allergyFlags} selected={educationIds} onChange={setEducationIds} />
          </Section>
            );
          })()}

            <div className="flex flex-wrap justify-end gap-2 pt-1 visit-toolbar" data-testid="save-preview-rx-bottom-container">
              <Button
                variant="outline"
                onClick={() => setDraftDialogOpen(true)}
                disabled={savingDraft || saving}
                data-testid="save-draft-btn-bottom"
                className="gap-2 h-11 px-5 text-sm border-slate-300"
                title="Save as a cloud draft — labs pending? Resume later from any PC via the Drafts page"
              >
                <FileClock size={16} /> {savingDraft ? "Saving…" : "Save as Draft"}
              </Button>
              <Button
                onClick={saveAll}
                disabled={saving}
                data-testid="save-visit-btn-bottom"
                className="bg-pink-600 hover:bg-pink-700 text-white gap-2 h-11 px-5 text-sm shadow"
                title="Finalize this visit and open the Rx print preview"
              >
                <Save size={16} /> {saving ? "Saving…" : "Save & Print"}
              </Button>
            </div>

            <AlertDialog open={draftDialogOpen} onOpenChange={setDraftDialogOpen}>
              <AlertDialogContent data-testid="draft-note-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2"><FileClock size={16} /> Park this visit as a draft</AlertDialogTitle>
                  <AlertDialogDescription>
                    Saved to the cloud — resume from any PC via the Drafts page. Optionally note what you're waiting for, so the Drafts list tells you why it's parked.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <Input
                  value={waitingFor}
                  onChange={(e) => setWaitingFor(e.target.value)}
                  placeholder="Waiting for… e.g. HbA1c, USG abdomen (optional)"
                  data-testid="draft-waiting-for-input"
                  onKeyDown={(e) => { if (e.key === "Enter") saveDraftNow(); }}
                />
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="draft-note-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={saveDraftNow} className="bg-pink-600 hover:bg-pink-700" data-testid="draft-note-save">Save draft</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <FamilyCard patientId={pid} />
          </div>
        </div>

        {/* Compare panel — sticky right rail showing the last visit for at-a-glance dose comparison */}
        {layout === "compare" && prevVisits[0] && (
          <div className="fixed right-4 top-24 w-[360px] max-h-[calc(100vh-8rem)] overflow-y-auto z-30 shadow-xl rounded-md" data-testid="compare-rail">
            <LastVisitPanel
              visit={prevVisits[0]}
              currentMedicines={medicines}
              currentDiagnosis={diagnosis}
              currentVitals={vitals}
              history={prevVisits}
              latestLabs={latestLabs}
              onCopyNextTests={copyNextTestsToPlan}
            />
          </div>
        )}
      </div>
      </div>

      {/* Check-before-add safety override dialog (queue — supports bundle adds) */}
      <AlertDialog open={pendingAdds.length > 0} onOpenChange={(o) => { if (!o) setPendingAdds((q) => q.slice(1)); }}>
        <AlertDialogContent data-testid="prescribe-guard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle size={18} /> Prescribing safety check
              {pendingAdds.length > 1 && <span className="text-[10px] font-normal text-slate-500">({pendingAdds.length} pending)</span>}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left">
                <div className="text-sm font-medium text-slate-800">
                  {pendingAdds[0]?.row?.brand_name || pendingAdds[0]?.row?.generic_name}
                </div>
                <ul className="space-y-1.5">
                  {(pendingAdds[0]?.issues || []).map((i, idx) => (
                    <li key={idx} className={`text-xs rounded p-2 border ${i.severity === "block" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                      {i.text}
                      {i.alt && (
                        <button
                          onClick={() => {
                            const p = pendingAdds[0];
                            const altRow = { ...i.alt.rx, _prov: { origin: "SAFETY_ENGINE", detail: "safety_alternative" } }; // PROV: ruling D2
                            if (p?.mode === "row") {
                              updRow(p.rowIndex, { ...emptyRow(), ...altRow, _uid: medicines[p.rowIndex]?._uid || uid() });
                              provLog({ event_type: "MED_ADDED", session_id: provSessionRef.current, patient_internal_id: pid, medicine: medEventFields(altRow), medicine_origin: "SAFETY_ENGINE", origin_detail: "safety_alternative" }); // PROV
                            } else { doAddMolecule(altRow); }
                            provLog({ event_type: "SAFETY_BLOCK_RESOLVED", session_id: provSessionRef.current, patient_internal_id: pid, medicine: medEventFields(p?.row || {}), physician_action: "MEDICINE_CHANGED", warning_text: (p?.issues || []).map((x) => x.text).join(" | "), evidence_text: `alternative: ${i.alt.rx.brand_name}` }); // PROV
                            toast.success(`Safe alternative added: ${i.alt.rx.brand_name}`);
                            setPendingAdds((q) => q.slice(1));
                          }}
                          className="mt-1.5 w-full text-left text-[11px] font-semibold px-2 py-1.5 rounded border border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 flex items-center gap-1.5"
                          data-testid={`prescribe-guard-alt-${idx}`}
                        >
                          ✓ Use instead: {i.alt.label}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="text-[11px] text-slate-500">You can override if clinically justified — the decision remains yours.</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="prescribe-guard-cancel"
              onClick={() => {
                const p = pendingAdds[0];
                if (p?.row?.brand_name || p?.row?.generic_name) provLog({ event_type: "SAFETY_BLOCK_RESOLVED", session_id: provSessionRef.current, patient_internal_id: pid, medicine: medEventFields(p.row), physician_action: p?.mode === "row" ? "MEDICINE_REMOVED" : "REJECTED", warning_text: (p?.issues || []).map((x) => x.text).join(" | ") }); // PROV
                if (p?.mode === "row") updRow(p.rowIndex, { ...emptyRow(), _uid: medicines[p.rowIndex]?._uid || uid() });
              }}
            >
              {pendingAdds[0]?.mode === "row" ? "Remove from Rx" : "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="prescribe-guard-override"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                const p = pendingAdds[0];
                if (p) {
                  provLog({ event_type: "SAFETY_BLOCK_RESOLVED", session_id: provSessionRef.current, patient_internal_id: pid, medicine: medEventFields(p.row), physician_action: "OVERRIDDEN", warning_text: (p.issues || []).map((x) => x.text).join(" | ") }); // PROV
                  if (p.mode !== "row") doAddMolecule(p.row);
                  setSafetyOverrides((prev) => [...prev, {
                    drug: p.row.brand_name || p.row.generic_name,
                    issues: (p.issues || []).map((i) => i.text.replace(/^[⛔⚠]\s*/, "")),
                    at: new Date().toISOString(),
                  }]);
                  toast.warning("Added with safety override — recorded in the audit trail", { duration: 5000 });
                }
              }}
            >
              {pendingAdds[0]?.mode === "row" ? "Override & Keep" : "Override & Add"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function Section({ title, children, testid, right, sectionId, defaultOpen, collapsible, icon, filled, summary, scrollTargetId, badge, autoOpenWhen }) {
  // Backward-compat wrapper: default sectionId to testid so every existing
  // call site automatically persists its open/collapsed state, and expose the
  // new props (filled, icon, summary, scrollTargetId) for the flow rail.
  return (
    <CollapsibleSection
      title={title}
      testid={testid}
      right={right}
      sectionId={sectionId ?? testid}
      defaultOpen={defaultOpen !== false}
      collapsible={collapsible !== false}
      icon={icon}
      filled={filled}
      summary={summary}
      badge={badge}
      autoOpenWhen={autoOpenWhen}
      scrollTargetId={scrollTargetId ?? testid}
    >
      {children}
    </CollapsibleSection>
  );
}
function F({ label, children }) {
  return (
    <div>
      <Label className="overline">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function ChipMulti({ items, selected, onToggle, colorMode, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const on = selected.includes(item);
        const base = on
          ? (colorMode === "warning" ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-pink-600 text-white border-pink-600")
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50";
        return (
          <button
            key={item}
            onClick={() => onToggle(item)}
            data-testid={`${testid}-${item.replace(/\s+/g, "-")}`}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium ${base}`}
          >
            {item}
          </button>
        );
      })}
    </div>
  );
}
